import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  Tooltip,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';

import { ja } from 'date-fns/locale/ja';
import { startOfWeek, addDays } from 'date-fns';

import MultiSelectCheckbox, { Option } from '../../common/MultiSelectCheckbox/MultiSelectCheckbox';
import MultiSelectWithCheckboxes from '../../common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';

import { apiClient } from '../../../lib/apiClient';
import { shareInFlightRequest } from '../../../lib/sharedApiRequest';
import { fetchLatestWeeklyReportPublicSettings } from '../../../api/weeklyReportPublicSettings';
import { subscribeWeeklyReportPublicScopeRefresh } from '../../../lib/weeklyReportPublicScopeRefresh';
import {
  buildWeeklySearchParams,
} from '../../../api/weeklySearch';
import { fetchOverwriteReportsWithOwnerAccess } from './overwriteOwnerSearch';
import {
  CREATOR_SYNC_BACKGROUND_INTERVAL_MS,
} from '../../../lib/creatorOptionsIndexedDb';
import { useProgressiveCreatorOptions } from '../../../hooks/useProgressiveCreatorOptions';
import {
  CREATOR_OPTION_DISPLAY_LIMIT,
  CREATOR_SEARCH_RESULT_LIMIT,
} from '../../../constants/creatorOptions';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  MasterResponse,
  MastersContextValue,
  Suggestion,
} from '../../WeeklyReport/SearchArea/WeeklyMasterProvider';

import { useUser } from '../../../context/UserContext';
import { useUserProfile } from '../../../context/UserProfileContext';
import { useLoginId } from '../../../hooks/useLoginId';
import { CLASSIFICATION_MARKET_TARGET_ATTR_NAME } from '../../../constants';
import { canAccessWeeklyReportByScope } from '../../../lib/weeklyReportOrganizationAccess';
import {
  ACCESS_CONTEXT_UPDATED_EVENT,
  getLatestWeeklyDataInvalidationMs,
} from '../../../lib/accessContextUpdate';

import {
  compareWeeklyReports,
  buildTeamSortIndexMap,
  attachKanaForWeeklyList,
} from '../../common/utils/weeklySort';
import {
  getWeeklyReportRegisteredDepartmentRawValues,
  buildWeeklyReportAccessMap,
  isAccessAllowedByEffectiveAccess,
  resolveEffectiveAccessForReportTypeValue,
  resolveReportTypeAccessOptions,
  type EffectiveReportTypeAccess,
  type ReportTypeAccessSetting,
} from '../../WeeklyReport/SearchArea/reportTypeAccess';
import {
  buildPreferredReportTypeIdByLabel,
} from '../../WeeklyReport/Content/helpers/reportTypeAccess';
import {
  buildSafeCreatorSearchSelection,
  UNKNOWN_CREATOR_LABEL,
} from './overwriteCreatorSelection';

type CreatorOptionWithDept = Option & {
  department_id?: string;
  user_id?: string | null;
  legacy?: boolean;
};

type UsersIndex = {
  byWrId: Map<string, any>;
  byEmail: Map<string, any>;
  byNameKey: Map<string, any>;
  // Cognito sub / uuid / user_id 等、いろいろなID → user レコード
  byAnyId: Map<string, any>;
  // いろいろなID → wr_id（検索/選択値の正規化に使う）
  anyIdToWrId: Map<string, string>;
  // いろいろなID → 氏名（表示用）
  anyIdToLabel: Map<string, string>;
};

const hasLabel = <T extends { label?: string | null }>(
  x: T | null | undefined,
): x is T & { label: string } =>
  !!x && typeof x.label === 'string' && x.label.trim().length > 0;

/* =========================================================
 * UI style
 * ======================================================= */

const baseCustomLabelStyle = {
  display: 'block',
  fontWeight: 500,
  fontSize: '12px',
  color: 'rgba(0, 0, 0, 0.87)',
  lineHeight: '1.5',
} as const;

const customLabelStyleNoMargin = {
  ...baseCustomLabelStyle,
  marginBottom: '5px',
} as const;

const multiSelectLabelNoMarginSx = {
  '& .MuiFormLabel-root': {
    marginBottom: '0px !important',
  },
  '& .MuiInputLabel-root': {
    marginBottom: '0px !important',
  },
} as const;

const commonInputPropsStyle = {
  fontSize: '0.9rem',
};

const datePickerSlotProps = {
  textField: {
    fullWidth: true,
    inputProps: { style: commonInputPropsStyle },
    size: 'small' as const,
  },
};

const textFieldSharedProps = {
  variant: 'outlined' as const,
  fullWidth: true,
  size: 'small' as const,
  inputProps: { style: commonInputPropsStyle },
};

const fieldLabelRowSx = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 24,
  mb: '2px',
  '@media (max-width:1550px)': {
    minHeight: 20,
    mb: '0px',
  },
} as const;

const fieldLabelTextSx = {
  ...baseCustomLabelStyle,
  marginBottom: 0,
  whiteSpace: 'nowrap',
  '@media (max-width:1550px)': {
    fontSize: '11px',
    lineHeight: 1,
  },
} as const;

// MultiSelectCheckbox 側の「内部ラベル」を見た目だけ消す（外側でラベルを出すため）
const hideInnerSelectLabelSx = {
  '& .MuiInputLabel-root, & .MuiFormLabel-root': {
    display: 'none',
  },
} as const;

// ラベル高さ（12px * line-height1.5 = 18px）に基づいてボタンの縦位置そろえ
const elementVerticalAlignPadding =
  `${parseFloat(baseCustomLabelStyle.fontSize) * parseFloat(baseCustomLabelStyle.lineHeight)}px`;

/* =========================================================
 * 型
 * ======================================================= */

type ApiReportType = {
  id: number;
  label: string;
  value?: string | null;
  color?: string | null;
  attr_id: number;
  department_id?: number;
  team_id?: number;
  order_number?: number;
};

const normalizeScopeCode = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFKC')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

type WeekRange = {
  start: Date;
  end: Date;
};

const parseTeamFiscalYear = (raw: unknown): number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return null;

  const s = raw.trim().toUpperCase();
  if (!s) return null;

  const fyMatch = s.match(/^FY(\d{2}|\d{4})$/);
  if (fyMatch) {
    const n = Number(fyMatch[1]);
    if (!Number.isFinite(n)) return null;
    return fyMatch[1].length === 2 ? 2000 + n : n;
  }

  if (/^\d{4}$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  return null;
};

const getTeamEndFiscalYear = (team: any): unknown =>
  team?.end_fiscal_year ?? team?.team_fiscal_year;

const isPastTeam = (teamFiscalYear: unknown): boolean =>
  parseTeamFiscalYear(teamFiscalYear) != null;

/* =========================================================
 * 日付ヘルパー
 * ======================================================= */

// 画面初期表示用: 「今週の週初め（月曜始まり）」を初期値に
const getDefaultStartDate = (): Date => {
  const today = new Date();
  return startOfWeek(today, { weekStartsOn: 1 });
};

// 画面初期表示用: 「今週の週末（金曜）」を初期値に
const getDefaultEndDate = (): Date => {
  const start = getDefaultStartDate();
  return addDays(start, 4);
};

// yyyy-mm-dd (ローカルタイム)
const formatLocalDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// yyyy-mm-dd -> Date（ローカル）
const parseLocalDate = (s: string): Date | null => {
  if (!s || typeof s !== 'string') return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
  const d = new Date(y, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

// 時刻を 00:00:00.000 にそろえる（比較用）
const stripTime = (d: Date): Date => {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getRecentPresetDateRange = (
  preset: Exclude<StartDatePreset, null>
): { start: Date; end: Date } => {
  const today = new Date();
  const thisWeekMon = startOfWeek(today, { weekStartsOn: 1 });
  const start =
    preset === 'this'
      ? thisWeekMon
      : preset === 'last'
        ? addDays(thisWeekMon, -7)
        : addDays(thisWeekMon, 7);
  const end = preset === 'this' ? stripTime(today) : addDays(start, 4);

  return { start, end };
};

// 検索開始日〜終了日に「かすっている週」をすべて返す
const buildHighlightedWeeksForRange = (
  startDate: Date | null,
  endDate: Date | null,
): WeekRange[] => {
  if (!startDate || !endDate) return [];

  const from = stripTime(startDate);
  const to = stripTime(endDate);

  const [min, max] = from <= to ? [from, to] : [to, from];

  // 週の開始日は「月曜」想定
  let currentStart = startOfWeek(min, { weekStartsOn: 1 });
  const weeks: WeekRange[] = [];

  while (currentStart <= max) {
    const currentEnd = addDays(currentStart, 6);
    weeks.push({
      start: stripTime(currentStart),
      end: stripTime(currentEnd),
    });
    currentStart = addDays(currentStart, 7);
  }

  return weeks;
};

type WeeklyHighlightDayProps = PickersDayProps<Date> & {
  highlightedWeeks?: WeekRange[];
  rangeStartDate?: Date | null;
  rangeEndDate?: Date | null;
  showRangeStartMarker?: boolean;
  showRangeEndMarker?: boolean;
};

// 週単位で帯がハイライトされるように PickersDay を拡張
const WeeklyHighlightDay: React.FC<WeeklyHighlightDayProps> = (props) => {
  const isDark = useTheme().palette.mode === 'dark';
  const {
    highlightedWeeks = [],
    rangeStartDate,
    rangeEndDate,
    showRangeStartMarker = false,
    showRangeEndMarker = false,
    day,
    outsideCurrentMonth,
    sx,
    selected,
    ...other
  } = props;

  const current = stripTime(day as Date);

  const matchedWeek = highlightedWeeks.find((week) => {
    const start = stripTime(week.start);
    const end = stripTime(week.end);
    return current >= start && current <= end;
  });

  const weekday = current.getDay();
  const isWeekday = weekday >= 1 && weekday <= 5; // 月〜金だけ帯表示

  const rangeStart = rangeStartDate ? stripTime(rangeStartDate) : null;
  const rangeEnd = rangeEndDate ? stripTime(rangeEndDate) : null;
  const isInExplicitDateRange =
    (!rangeStart || current >= rangeStart) && (!rangeEnd || current <= rangeEnd);
  const isInHighlightedWeekAndWeekday = !!matchedWeek && isWeekday && isInExplicitDateRange;
  const isSameRangeBoundary =
    !!rangeStart && !!rangeEnd && rangeStart.getTime() === rangeEnd.getTime();
  const isRangeStartMarker =
    showRangeStartMarker &&
    !!rangeStart &&
    !outsideCurrentMonth &&
    !isSameRangeBoundary &&
    rangeStart.getTime() === current.getTime();
  const isRangeEndMarker =
    showRangeEndMarker &&
    !!rangeEnd &&
    !outsideCurrentMonth &&
    !isSameRangeBoundary &&
    rangeEnd.getTime() === current.getTime();
  const effectiveSelected = Boolean(selected && !isRangeStartMarker && !isRangeEndMarker);

  let tooltipTitle: string | undefined;
  if (isInHighlightedWeekAndWeekday) {
    // 週報の期間は「月〜金」で固定
    const weekStart = startOfWeek(matchedWeek.start, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 4); // 月〜金

    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}/${m}/${dd}`;
    };

    tooltipTitle = `${fmt(weekStart)}〜${fmt(weekEnd)} の週報です`;
  }

  // selected === true のときは背景色を上書きしない
  const dayNode = (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={effectiveSelected}
      sx={{
        ...sx,
        ...(isInHighlightedWeekAndWeekday && !effectiveSelected
          ? {
              backgroundColor: '#d0f0c0',
              ...(isDark ? { color: '#0f172a' } : {}),
              borderRadius: 0,
              '&:hover, &:focus': {
                backgroundColor: '#b2dfdb',
              },
            }
          : {}),
        ...(isRangeStartMarker
          ? {
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              boxShadow: 'inset 0 0 0 1px #60a5fa',
              color: '#0f172a',
              '&:hover, &:focus': {
                backgroundColor: '#bfdbfe',
              },
            }
          : {}),
        ...(isRangeEndMarker
          ? {
              backgroundColor: '#2563eb',
              borderRadius: '50%',
              color: '#fff',
              '&:hover, &:focus': {
                backgroundColor: '#1d4ed8',
              },
            }
          : {}),
      }}
    />
  );

  if (tooltipTitle) {
    return (
      <Tooltip
        title={tooltipTitle}
        arrow
        // Tooltip 自体が hover 判定を持たないようにする（マウスが外れたら素直に閉じる）
        disableInteractive
        // Tooltip が表示中でも下の週をクリック/ホバーできるようにする（イベントを邪魔しない）
        slotProps={{
          popper: {
            sx: {
              pointerEvents: 'none',
            },
          },
        }}
      >
        <span>{dayNode}</span>
      </Tooltip>
    );
  }

  return dayNode;
};

type OverwriteSearchAreaProps = {
  onSearchResults: (results: any[]) => void;
  /** モーダルが開いているか（Dialog の open をそのまま渡す） */
  isOpen: boolean;
};

/* =========================================================
 * キャッシュ（localStorage）
 * ======================================================= */

// StartDatePreset を cache/type でも使うのでコンポーネント外に出す
type StartDatePreset = 'this' | 'last' | 'next' | null;
type DateSearchMode = 'recent' | 'range';
const RECENT_PRESET_ORDER = ['last', 'this', 'next'] as const;
type RecentPreset = (typeof RECENT_PRESET_ORDER)[number];

const RECENT_PRESET_TONE: Record<
  RecentPreset,
  {
    surface: string;
    border: string;
    buttonSurface: string;
    buttonBorder: string;
    buttonHoverSurface: string;
    buttonHoverBorder: string;
  }
> = {
  last: {
    surface: '#f8fafc',
    border: '#dbe3ee',
    buttonSurface: '#ffffff',
    buttonBorder: '#cbd5e1',
    buttonHoverSurface: '#f1f5f9',
    buttonHoverBorder: '#94a3b8',
  },
  this: {
    surface: '#dbeafe',
    border: '#93c5fd',
    buttonSurface: '#bfdbfe',
    buttonBorder: '#60a5fa',
    buttonHoverSurface: '#93c5fd',
    buttonHoverBorder: '#3b82f6',
  },
  next: {
    surface: '#93c5fd',
    border: '#3b82f6',
    buttonSurface: '#60a5fa',
    buttonBorder: '#2563eb',
    buttonHoverSurface: '#3b82f6',
    buttonHoverBorder: '#1d4ed8',
  },
};

const RECENT_PRESET_TONE_DARK: typeof RECENT_PRESET_TONE = {
  last: {
    surface: '#1e293b',
    border: '#334155',
    buttonSurface: '#1e293b',
    buttonBorder: '#334155',
    buttonHoverSurface: '#243044',
    buttonHoverBorder: '#475569',
  },
  this: {
    surface: '#243044',
    border: '#334155',
    buttonSurface: '#243044',
    buttonBorder: '#475569',
    buttonHoverSurface: '#2d3f55',
    buttonHoverBorder: '#475569',
  },
  next: {
    surface: '#2d3f55',
    border: '#475569',
    buttonSurface: '#2d3f55',
    buttonBorder: '#475569',
    buttonHoverSurface: '#334155',
    buttonHoverBorder: '#64748b',
  },
};

// 検索条件は前回値を引き継ぐ仕様のためv2を維持する。
// 旧発行者IDは復元後の正規化で安全に除外し、検索結果だけをv3で破棄する。
const OVERWRITE_SEARCH_CACHE_VERSION = 2 as const;
const OVERWRITE_SEARCH_RESULTS_CACHE_VERSION = 3 as const;
const OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_VERSION = 1 as const;
const OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_KEY_PREFIX = 'overwrite-search-creator-options-cache';
const WEEKLY_REPORT_UPDATED_EVENT = 'weekly-report-updated';
const WEEKLY_REPORT_UPDATED_AT_KEY = 'weekly-report-updated-at';
const OVERWRITE_SEARCH_RESULTS_CACHE_TTL_MS = 30 * 60 * 1000;
const OVERWRITE_SEARCH_RESULTS_CACHE_MAX_CHARS = 1_500_000;

type OverwriteSearchCriteria = {
  startDate: string | null; // yyyy-mm-dd
  endDate: string | null;   // yyyy-mm-dd
  freeWord: string;
  selectedReportTypes: string[];
  selectedTeams: string[];
  selectedCreateByIds: string[];
  selectedCreateByOptions?: Array<{
    id: string;
    value: string;
    department_id?: string;
    user_id?: string | null;
    legacy?: boolean;
  }>;
  selectedDeptIds: string[];
  deptTouched: boolean;
  createByTouched: boolean;
  // ここに統一（null も保存対象）
  startDatePreset?: StartDatePreset;
  dateSearchMode?: DateSearchMode;
};
type OverwriteSearchCriteriaCacheV2 = {
  version: typeof OVERWRITE_SEARCH_CACHE_VERSION;
  savedAt: string; // ISO
  criteria: OverwriteSearchCriteria;
};

type OverwriteSearchResultsCacheV3 = {
  version: typeof OVERWRITE_SEARCH_RESULTS_CACHE_VERSION;
  savedAt: string; // ISO
  criteriaSig: string;
  results?: any[];
};

type OverwriteSearchCacheRead = {
  criteria?: OverwriteSearchCriteria;
  results?: any[];
};

type OverwriteSearchCreatorOptionsCache = {
  version: typeof OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_VERSION;
  savedAt: string;
  options: Array<{
    id: string;
    value: string;
    department_id?: string;
    user_id?: string | null;
    legacy?: boolean;
  }>;
};

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): boolean => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (_e) {
    console.warn(`[OverwriteSearchArea] localStorage set failed: ${key}`, _e);
    return false;
  }
};

const safeLocalStorageRemove = (key: string): boolean => {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (_e) {
    console.warn(`[OverwriteSearchArea] localStorage remove failed: ${key}`, _e);
    return false;
  }
};

const safeJsonParse = <T,>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch (_e) {
    return null;
  }
};

const safeParseIsoToMs = (iso: string | null): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

const readWeeklyReportUpdatedAtMs = (): number | null => {
  try {
    return safeParseIsoToMs(localStorage.getItem(WEEKLY_REPORT_UPDATED_AT_KEY));
  } catch {
    return null;
  }
};

const readLatestWeeklyDataInvalidationMs = (): number =>
  getLatestWeeklyDataInvalidationMs(readWeeklyReportUpdatedAtMs());

const isAbortError = (err: unknown): boolean => {
  if (err instanceof DOMException) return err.name === 'AbortError';
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: string }).name === 'AbortError'
  );
};

// 記入者などの比較で「全角スペース」「余分な空白」「記号」吸収
const normalizeName = (s: string) => {
  return String(s ?? '')
    .replace(/\u3000/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// スペース有無の揺れ + 記号の揺れを潰す（山田太郎 / 山田 太郎 / 山田・太郎 を同一視）
// カンマも潰す（"YAMADA, ZIROU" 対策）
const normalizeNameKey = (s: string) => {
  return normalizeName(s)
    .replace(/[\s,，・･\.\-‐-‒–—―_]/g, '')
    .toLowerCase();
};

const normalizeEmail = (s: any) => {
  return String(s ?? '').trim().toLowerCase();
};

// department 文字列（例: XQ4 / XR2）を比較用に正規化
const normalizeDeptCode = (s: any) => {
  return normalizeName(String(s ?? ''))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

// /get_all_user_info のユーザーから department 文字列を取り出す
// ※バックエンドのキー揺れに強くする
const extractDeptCodeLike = (row: any): string | undefined => {
  if (!row) return undefined;

  const v =
    row.department ??
    row.dept ??
    row.division ??
    row.department_name ??
    row.dept_name ??
    row.division_name ??
    row.departmentCode ??
    row.department_code ??
    row.deptCode ??
    row.dept_code ??
    row.divisionCode ??
    row.division_code ??
    null;

  const s = String(v ?? '').trim();
  if (!s) return undefined;

  return normalizeDeptCode(s);
};

const uniq = (arr: string[]) => Array.from(new Set(arr.map((v) => String(v))));

const runMicrotask = (fn: () => void) => {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(fn);
  } else {
    Promise.resolve().then(fn);
  }
};

const extractWrIdLike = (row: any): string | null => {
  if (!row) return null;

  const custom =
    row['custom:wr_id'] ??
    row.custom_wr_id ??
    row.customWrId ??
    null;

  if (custom != null) return String(custom);

  const v =
    row.wr_id ??
    row.wrId ??
    row.user_wr_id ??
    row.userWrId ??
    row.user_id ??
    row.userId ??
    row.id ??
    null;

  if (v == null) return null;

  return String(v);
};

const extractFullNameLike = (row: any): string => {
  if (!row) return '';

  const byLabel =
    row.label ??
    row.value ??
    row.name ??
    row.full_name ??
    row.fullName ??
    row.user_name ??
    row.userName ??
    row.display_name ??
    row.displayName ??
    '';

  const s1 = String(byLabel ?? '').trim();
  if (s1) return s1;

  const ln =
    row.last_name ??
    row.family_name ??
    row.lastname ??
    row.lastName ??
    row.last ??
    '';
  const fn =
    row.first_name ??
    row.given_name ??
    row.firstname ??
    row.firstName ??
    row.first ??
    '';

  const s2 = `${String(ln ?? '').trim()} ${String(fn ?? '').trim()}`.trim();
  if (s2 && s2 !== 'undefined undefined') return s2;

  return '';
};

const extractEmailCandidates = (row: any): string[] => {
  if (!row) return [];
  const keys = [
    'email',
    'mail',
    'mail_address',
    'email_address',
    'loginId',
    'login_id',
    'user_email',
    'user_mail',
  ];

  const out: string[] = [];
  keys.forEach((k) => {
    const v = (row as any)[k];
    if (!v) return;
    out.push(normalizeEmail(v));
  });

  return uniq(out).filter((x) => x.length > 0);
};

const extractAnyIds = (row: any): string[] => {
  if (!row) return [];

  const candidates: any[] = [
    row.sub,
    row.cognito_sub,
    row.cognitoSub,
    row.user_sub,
    row.userSub,

    row.uuid,
    row.user_uuid,
    row.userUuid,

    row.user_id,
    row.userId,
    row.id,

    row['custom:wr_id'],
    row.custom_wr_id,
    row.customWrId,

    row.wr_id,
    row.wrId,
  ];

  const out = candidates
    .filter((v) => v != null)
    .map((v) => String(v).trim())
    .filter((v) => v.length > 0);

  return uniq(out);
};

const extractDeptIdLike = (
  row: any,
  deptLabelToIdMap: Record<string, string>,
): string | undefined => {
  if (!row) return undefined;

  const direct =
    row.department_id ??
    row.departmentId ??
    row.dept_id ??
    row.deptId ??
    row.division_id ??
    row.divisionId ??
    null;

  if (direct != null && String(direct).trim().length > 0) {
    return String(direct).trim();
  }

  // ラベル系
  const label =
    row.department ??
    row.department_name ??
    row.dept ??
    row.dept_name ??
    row.division ??
    row.division_name ??
    null;

  if (label != null) {
    const rawLabel = String(label).trim();
    const mapped = deptLabelToIdMap[rawLabel];
    if (mapped) return String(mapped);

    const normalizedRaw = normalizeDeptCode(rawLabel);
    if (normalizedRaw) {
      const normalizedHit = Object.entries(deptLabelToIdMap).find(
        ([deptLabel]) => normalizeDeptCode(deptLabel) === normalizedRaw,
      );
      if (normalizedHit?.[1]) return String(normalizedHit[1]);
    }
  }

  return undefined;
};

const buildUsersIndex = (
  usersMaster: any[],
): UsersIndex => {
  const byWrId = new Map<string, any>();
  const byEmail = new Map<string, any>();
  const byNameKey = new Map<string, any>();
  const byAnyId = new Map<string, any>();
  const anyIdToWrId = new Map<string, string>();
  const anyIdToLabel = new Map<string, string>();

  usersMaster.forEach((u) => {
    const wrId = extractWrIdLike(u);
    const fullName = extractFullNameLike(u);
    const nameKey = normalizeNameKey(fullName);

    if (wrId) {
      if (!byWrId.has(String(wrId))) byWrId.set(String(wrId), u);
      anyIdToWrId.set(String(wrId), String(wrId));
      if (fullName) anyIdToLabel.set(String(wrId), fullName);
    }

    extractEmailCandidates(u).forEach((mail) => {
      const key = normalizeEmail(mail);
      if (!key) return;
      if (!byEmail.has(key)) byEmail.set(key, u);
      if (wrId) anyIdToWrId.set(key, String(wrId));
      if (fullName) anyIdToLabel.set(key, fullName);
    });

    if (nameKey) {
      if (!byNameKey.has(nameKey)) byNameKey.set(nameKey, u);
      if (wrId) anyIdToWrId.set(nameKey, String(wrId));
      if (fullName) anyIdToLabel.set(nameKey, fullName);
    }

    extractAnyIds(u).forEach((id) => {
      if (!id) return;
      if (!byAnyId.has(id)) byAnyId.set(id, u);
      if (wrId) anyIdToWrId.set(id, String(wrId));
      if (fullName) anyIdToLabel.set(id, fullName);
    });
  });

  return {
    byWrId,
    byEmail,
    byNameKey,
    byAnyId,
    anyIdToWrId,
    anyIdToLabel,
  };
};

type CreatorFromWeeklyRequiredItems = {
  wr_id: number;
  label: string;
};

const OverwriteSearchArea: React.FC<OverwriteSearchAreaProps> = ({
  onSearchResults,
  isOpen,
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  // ---------- ログインID（キャッシュキー用） ----------
  const currentUser = useUser();
  const { profile, loaded: profileLoaded } = useUserProfile();
  // Hosted UI（SSO）ログインでは signInDetails が無いため、email へフォールバックした値も使う
  const fallbackLoginId = useLoginId();
  const normalizedLoginId: string | null = useMemo(() => {
    const u: any = currentUser;
    const p: any = profile;
    const candidates = [
      p?.email,
      p?.mail_address,
      p?.mail,
      p?.email_address,
      p?.loginId,
      p?.login_id,
      u?.signInDetails?.loginId,
      u?.loginId,
      u?.email,
      u?.mail,
      u?.attributes?.email,
      u?.signInUserSession?.idToken?.payload?.email,
      fallbackLoginId,
    ];
    for (const raw of candidates) {
      if (!raw) continue;
      const normalized = String(raw).trim().toLowerCase();
      if (normalized.length > 0) return normalized;
    }
    return null;
  }, [currentUser, profile, fallbackLoginId]);

  const cacheKey = useMemo(() => {
    if (!normalizedLoginId) return null;
    return `overwrite-search-area:v${OVERWRITE_SEARCH_CACHE_VERSION}:${normalizedLoginId}`;
  }, [normalizedLoginId]);

  const creatorOptionsLocalStorageBaseKey = useMemo(() => {
    return `${OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_KEY_PREFIX}:v${OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_VERSION}:${normalizedLoginId ?? 'unknown'}`;
  }, [normalizedLoginId]);

  // 最初に確定した cacheKey を固定で使う（途中で null → 値 に変わっても復元を落とさない）
  const stableCacheKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (cacheKey && !stableCacheKeyRef.current) {
      stableCacheKeyRef.current = cacheKey;
    }
  }, [cacheKey]);

  const buildCriteriaSig = useCallback((criteria: OverwriteSearchCriteria): string => {
    try {
      return JSON.stringify(criteria);
    } catch {
      return '';
    }
  }, []);

  const readCache = useCallback((baseKey: string | null): OverwriteSearchCacheRead | null => {
    if (!baseKey) return null;
    if (typeof window === 'undefined') return null;

    const criteriaKey = `${baseKey}:criteria`;
    const resultsKey = `${baseKey}:results`;
    const criteriaRaw = safeLocalStorageGet(criteriaKey);
    const criteriaParsed = criteriaRaw
      ? safeJsonParse<OverwriteSearchCriteriaCacheV2>(criteriaRaw)
      : null;
    const criteria =
      criteriaParsed?.version === OVERWRITE_SEARCH_CACHE_VERSION
        ? criteriaParsed.criteria
        : undefined;

    const resultsRaw = safeLocalStorageGet(resultsKey);
    const resultsParsed = resultsRaw
      ? safeJsonParse<OverwriteSearchResultsCacheV3>(resultsRaw)
      : null;
    const resultsSavedAtMs = safeParseIsoToMs(resultsParsed?.savedAt ?? null) ?? 0;
    const latestReportUpdateMs = readLatestWeeklyDataInvalidationMs();
    const resultsFresh =
      resultsSavedAtMs > 0 &&
      resultsSavedAtMs >= latestReportUpdateMs &&
      Date.now() - resultsSavedAtMs < OVERWRITE_SEARCH_RESULTS_CACHE_TTL_MS;
    const criteriaSig = criteria ? buildCriteriaSig(criteria) : '';
    const results =
      resultsParsed?.version === OVERWRITE_SEARCH_RESULTS_CACHE_VERSION &&
      resultsFresh &&
      resultsParsed.criteriaSig === criteriaSig &&
      Array.isArray(resultsParsed.results)
        ? resultsParsed.results
        : undefined;

    if (resultsRaw && !results) {
      safeLocalStorageRemove(resultsKey);
    }

    if (!criteria && !results) return null;

    return { criteria, results };
  }, [buildCriteriaSig]);

  // ---------- 日付・フリーワード ----------
  const [startDate, setStartDate] = useState<Date | null>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());
  const [freeWord, setFreeWord] = useState<string>('');

  // 日付プリセット（週報連画面と同様に「先週」「本日」を用意）
  type EndDatePreset = 'today' | null;

  const [startDatePreset, setStartDatePreset] = useState<StartDatePreset>(null);
  const [endDatePreset, setEndDatePreset] = useState<EndDatePreset>('today');
  const [dateSearchMode, setDateSearchMode] = useState<DateSearchMode>('range');
  const recentPresetRef = useRef<RecentPreset>('this');
  const rangeDateSnapshotRef = useRef<{ startDate: Date | null; endDate: Date | null } | null>(
    null
  );

  // open 時に使う「今回のデフォルト日付」を保持（初回自動検索のズレ防止）
  const openDefaultDatesRef = useRef<{ sd: Date; ed: Date } | null>(null);

  const applyDefaultDateRange = useCallback(() => {
    const sd = getDefaultStartDate();
    const ed = getDefaultEndDate();

    // 変更後：デフォルトではチェックを入れない
    setStartDatePreset(null);
    setDateSearchMode('range');

    setEndDatePreset('today');

    setStartDate(sd);
    setEndDate(ed);

    return { sd, ed };
  }, []);

  const applyStartDatePreset = useCallback((preset: Exclude<StartDatePreset, null>) => {
    const { start, end } = getRecentPresetDateRange(preset);

    setStartDatePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setEndDatePreset(null);
  }, []);

  // モーダルを開いたタイミングの制御
  // ※open直後は cache hydrate により start/end が書き換わり得るため、ここでは日付を確定しない
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      prevOpenRef.current = false;

      // close で「初回検索用スナップショット」を破棄（次回openでズレないように）
      openDefaultDatesRef.current = null;
      return;
    }

    if (prevOpenRef.current) return; // open中の再実行を防止
    prevOpenRef.current = true;

    // open直後はまだ cache hydrate 前の可能性があるので一旦破棄し、
    // didHydrateFromCache 後の effect で「UIに表示されている日付」に同期してから初回検索させる
    openDefaultDatesRef.current = null;

    // 日付エラーだけはクリア
    setStartDatePickerError(null);
    setEndDatePickerError(null);
  }, [isOpen]);


  // ===== 日付のバリデーション =====
  const [startDatePickerError, setStartDatePickerError] = useState<any>(null);
  const [endDatePickerError, setEndDatePickerError] = useState<any>(null);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  useEffect(() => {
    if (startDatePreset) {
      recentPresetRef.current = startDatePreset;
    }
  }, [startDatePreset]);

  const activateRecentMode = useCallback(() => {
    setEndDatePickerOpen(false);
    rangeDateSnapshotRef.current = {
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
    };

    const nextPreset = startDatePreset ?? recentPresetRef.current ?? 'this';
    setDateSearchMode('recent');
    applyStartDatePreset(nextPreset);
  }, [startDate, endDate, startDatePreset, applyStartDatePreset]);

  const activateRangeMode = useCallback(() => {
    const keepCurrentDates = dateSearchMode === 'recent' || startDatePreset != null;

    setDateSearchMode('range');
    setEndDatePickerOpen(false);
    setStartDatePreset(null);

    if (keepCurrentDates) {
      rangeDateSnapshotRef.current = null;
      return;
    }

    const snapshot = rangeDateSnapshotRef.current;
    if (!snapshot) return;

    if (snapshot.startDate) setStartDate(new Date(snapshot.startDate));
    if (snapshot.endDate) setEndDate(new Date(snapshot.endDate));
  }, [dateSearchMode, startDatePreset]);

  const handleDateSearchModeChange =
    (mode: DateSearchMode) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) return;
      if (mode === 'recent') {
        activateRecentMode();
        return;
      }
      activateRangeMode();
    };

  const currentRecentPreset: RecentPreset = (
    startDatePreset ??
    recentPresetRef.current ??
    'this'
  ) as RecentPreset;
  const recentPresetTone = (isDark ? RECENT_PRESET_TONE_DARK : RECENT_PRESET_TONE)[
    currentRecentPreset
  ];
  const recentPresetButtonSx = {
    minWidth: 70,
    whiteSpace: 'nowrap',
    px: 0.9,
    borderRadius: '10px',
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: 'none',
    color: isDark ? '#38bdf8' : '#2563eb',
    backgroundColor: recentPresetTone.buttonSurface,
    borderColor: recentPresetTone.buttonBorder,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: recentPresetTone.buttonHoverSurface,
      borderColor: recentPresetTone.buttonHoverBorder,
    },
    '&.Mui-disabled': {
      color: '#94a3b8',
      backgroundColor: isDark ? '#243044' : '#f1f5f9',
      borderColor: isDark ? '#334155' : '#e2e8f0',
    },
    '@media (min-width:1200px)': {
      minWidth: 64,
      px: 0.75,
      '& .MuiButton-startIcon': {
        ml: -0.25,
        mr: 0.35,
      },
      '& .MuiButton-endIcon': {
        ml: 0.35,
        mr: -0.25,
      },
      '& .MuiSvgIcon-root': {
        fontSize: 17,
      },
    },
  };

  const shiftRecentPreset = useCallback(
    (direction: -1 | 1) => {
      const currentIdx = RECENT_PRESET_ORDER.indexOf(currentRecentPreset);
      const nextIdx = Math.max(0, Math.min(RECENT_PRESET_ORDER.length - 1, currentIdx + direction));
      const nextPreset = RECENT_PRESET_ORDER[nextIdx];
      if (!nextPreset || nextPreset === currentRecentPreset) return;
      applyStartDatePreset(nextPreset);
    },
    [currentRecentPreset, applyStartDatePreset]
  );

  const dateRangeErrorMsg = useMemo(() => {
    if (!startDate || !endDate) return '';
    if (stripTime(endDate) < stripTime(startDate)) {
      return '終了日付は開始日付以降を指定してください';
    }
    return '';
  }, [startDate, endDate]);

  const startDateHelperText = useMemo(() => {
    if (!startDatePickerError) return '';
    const err = String(startDatePickerError);
    if (err === 'maxDate') return '開始日付は終了日付以前を指定してください';
    if (err === 'invalidDate') return '開始日付が不正です';
    return '開始日付が不正です';
  }, [startDatePickerError]);

  const endDateHelperText = useMemo(() => {
    if (dateRangeErrorMsg) return dateRangeErrorMsg;
    if (!endDatePickerError) return '';
    const err = String(endDatePickerError);
    if (err === 'minDate') return '終了日付は開始日付以降を指定してください';
    if (err === 'invalidDate') return '終了日付が不正です';
    return '終了日付が不正です';
  }, [endDatePickerError, dateRangeErrorMsg]);

  const hasDateError =
    Boolean(startDatePickerError) ||
    Boolean(endDatePickerError) ||
    Boolean(dateRangeErrorMsg);
  const isDateRangeIncomplete = !startDate || !endDate;
  const recentPresetLabel = useMemo(() => {
    if (currentRecentPreset === 'last') return '先週 Last week';
    if (currentRecentPreset === 'next') return '来週 Next week';
    return '今週 This week';
  }, [currentRecentPreset]);
  const recentRangeLabel = useMemo(() => {
    if (!startDate || !endDate) return '-';
    const toSlash = (d: Date) =>
      `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
    return `${toSlash(startDate)}～${toSlash(endDate)}`;
  }, [startDate, endDate]);

  // 週ハイライト用（開始日付～終了日付にかかる週）
  const highlightedWeeks = useMemo(
    () => buildHighlightedWeeksForRange(startDate, endDate),
    [startDate, endDate],
  );

  // ---------- マスター全体 ----------
  const [state, setState] = useState<MastersContextValue>({
    loading: true,
    error: null,
    data: null,
  });

  // 初回自動検索が完了したか（モーダルを開いた“その1回”だけ走らせる）
  const [didInitialSearch, setDidInitialSearch] = useState(false);

  // 「全チーム一覧」「全ユーザー一覧」も別で取る
  const [teamsMaster, setTeamsMaster] = useState<any[]>([]);
  const [teamClassificationsMaster, setTeamClassificationsMaster] = useState<any[]>([]);
  const [usersMaster, setUsersMaster] = useState<any[]>([]);

  // usersMaster の取得完了フラグ（成功/失敗どちらでも true）
  const [didFetchUsersMaster, setDidFetchUsersMaster] = useState(false);
  const [creatorOptionsByDate, setCreatorOptionsByDate] =
    useState<CreatorOptionWithDept[] | null>(null);
  const [creatorOptionsLoadingRemaining, setCreatorOptionsLoadingRemaining] =
    useState(false);
  const [creatorOptionsResolvedKey, setCreatorOptionsResolvedKey] = useState<string | null>(null);
  const [creatorSelectionNormalizedKey, setCreatorSelectionNormalizedKey] = useState<string | null>(null);
  const [creatorSyncTick, setCreatorSyncTick] = useState(0);
  const creatorSyncForceRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setInterval(
      () => setCreatorSyncTick((value) => value + 1),
      CREATOR_SYNC_BACKGROUND_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, [isOpen]);

  // ---------- deptOptions（課候補） ----------
  const deptOptions: Suggestion[] = useMemo(() => {
    const list = (state.data as MasterResponse | null)?.departments ?? [];

    return list
      .filter(hasLabel)
      .map((d: any) => {
        const idNum = Number(d?.id);
        if (!Number.isFinite(idNum)) return null;

        return {
          id: idNum,
          label: String(d.label),
        } as Suggestion;
      })
      .filter((x): x is Suggestion => x != null);
  }, [state.data]);

  // label("◯◯課") -> id("1")
  const deptLabelToIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = (state.data as MasterResponse | null)?.departments ?? [];
    list.forEach((d: any) => {
      if (d?.label != null && d?.id != null) {
        map[String(d.label)] = String(d.id);
      }
    });
    return map;
  }, [state.data]);

  const deptIdToLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = (state.data as MasterResponse | null)?.departments ?? [];
    list.forEach((d: any) => {
      if (d?.id == null || d?.label == null) return;
      map[String(d.id)] = String(d.label);
    });
    return map;
  }, [state.data]);

  // ---------- ユーザー索引（email/nameKey など） ----------
  const usersIndex = useMemo(() => {
    return buildUsersIndex(usersMaster);
  }, [usersMaster]);

  // ---------- weekly-required-items creators（wr_id,label） ----------
  const creatorsMaster: CreatorFromWeeklyRequiredItems[] = useMemo(() => {
    const list = (state.data as any)?.creators ?? [];
    if (!Array.isArray(list)) return [];
    return list
      .filter((c: any) => c && c.wr_id != null && typeof c.label === 'string')
      .map((c: any) => ({ wr_id: Number(c.wr_id), label: String(c.label) }))
      .filter((c: any) => Number.isFinite(c.wr_id) && c.label.trim().length > 0);
  }, [state.data]);

  // wr_id -> creators.label（fallback 用）
  const creatorsWrIdLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    creatorsMaster.forEach((c) => {
      const id = String(c.wr_id);
      const label = String(c.label ?? '').trim();
      if (id && label) m.set(id, label);
    });
    return m;
  }, [creatorsMaster]);

  // normalizeNameKey(creators.label) -> wr_id（変換用）
  const creatorWrIdByNameKey = useMemo(() => {
    const m = new Map<string, string>();
    creatorsMaster.forEach((c) => {
      const key = normalizeNameKey(String(c.label ?? '').trim());
      const id = c.wr_id;
      if (!key) return;
      if (!Number.isFinite(id)) return;

      // 同名が複数いる場合は先勝ち（必要ならログ出し等で検知）
      if (!m.has(key)) {
        m.set(key, String(id));
      }
    });
    return m;
  }, [creatorsMaster]);

  // usersMaster 1件から wr_id を解決する（usersMaster.wr_id が無い前提）
  const resolveWrIdFromUserMaster = useCallback((u: any): string | null => {
    if (!u) return null;

    // usersMaster 側に wr_id が入っている環境なら最優先
    const direct = extractWrIdLike(u);
    if (direct) return String(direct);

    const fullName = extractFullNameLike(u);
    if (!fullName) return null;

    const key = normalizeNameKey(fullName);
    const mapped = creatorWrIdByNameKey.get(key) ?? null;

    if (mapped) return String(mapped);

    return null;
  }, [creatorWrIdByNameKey]);

  // ---------- creators の wr_id -> label（表示用） ----------
  // ★要件対応：表示は usersMaster 優先、見つからない時だけ creators をフォールバック
  const creatorExternalLabelMap = useMemo(() => {
    const m = new Map<string, string>();

    usersMaster.forEach((u: any) => {
      const id = resolveWrIdFromUserMaster(u);
      const label = extractFullNameLike(u);
      if (id && label) {
        m.set(String(id), String(label));
      }
    });

    creatorsWrIdLabelMap.forEach((label, id) => {
      if (!m.has(String(id))) {
        m.set(String(id), String(label));
      }
    });

    return m;
  }, [usersMaster, resolveWrIdFromUserMaster, creatorsWrIdLabelMap]);

  // ---------- 週報分類（reportTypes） ----------
  const reportOptionsRaw = useMemo(() => {
    const list = ((state.data as MasterResponse | null)?.reportTypes as ApiReportType[] | undefined) ?? [];

    const withLabel = list.filter(hasLabel).map((item) => ({
      id: String(item.id),
      value: item.value ?? item.label,
      label: item.label,
      color: item.color ?? null,
      attr_id: item.attr_id,
      department_id: item.department_id ? String(item.department_id) : undefined,
      team_id: item.team_id,
      order_number: item.order_number,
    }));

    const seen = new Set<string>();

    return withLabel.filter((opt) => {
      const key = `${opt.attr_id}::${opt.department_id ?? ''}::${opt.label}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [state.data]);

  const rawReportTypeIdSet = useMemo(() => {
    const list = ((state.data as MasterResponse | null)?.reportTypes as ApiReportType[] | undefined) ?? [];
    return new Set(
      list
        .map((rt) => String((rt as any)?.id ?? '').trim())
        .filter((id) => id.length > 0),
    );
  }, [state.data]);

  const [reportTypeAccessById, setReportTypeAccessById] = useState<
    Record<string, ReportTypeAccessSetting> | null
  >(null);
  const [publicScopeRevision, setPublicScopeRevision] = useState(0);
  const isReportTypeAccessReady = reportTypeAccessById !== null;

  useEffect(
    () => subscribeWeeklyReportPublicScopeRefresh(() => {
      setReportTypeAccessById(null);
      onSearchResults([]);
      setPublicScopeRevision((value) => value + 1);
    }),
    [onSearchResults],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setReportTypeAccessById(null);
        const rows = await fetchLatestWeeklyReportPublicSettings();
        if (cancelled) return;
        setReportTypeAccessById(
          buildWeeklyReportAccessMap(rows, rawReportTypeIdSet, state.data ?? {})
        );
      } catch (error) {
        if (cancelled) return;
        setReportTypeAccessById(null);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawReportTypeIdSet, state.data?.departments, state.data?.teams, publicScopeRevision]);

  const teamClassNameByIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = Array.isArray(teamClassificationsMaster)
      ? teamClassificationsMaster
      : [];
    list.forEach((row: any) => {
      const id = String((row as any)?.class_id ?? (row as any)?.id ?? '').trim();
      const name = String(
        (row as any)?.class_name ??
          (row as any)?.classification_name ??
          (row as any)?.name ??
          ''
      ).trim();
      if (!id || !name) return;
      map[id] = name;
    });
    return map;
  }, [teamClassificationsMaster]);

  // ---------- チーム関連 ----------
  const allTeamOptionsRaw: (Option & { department_id?: string })[] = useMemo(() => {
    return teamsMaster
      .filter(
        (t) =>
          t.status === 'Y' &&
          typeof t.label === 'string' &&
          t.label.trim().length > 0,
      )
      .map((t) => ({
        id: String(t.team_id),
        value: String(t.label ?? '').trim(),
        groupLabel: (() => {
          if (isPastTeam(getTeamEndFiscalYear(t))) {
            return '過去チーム';
          }
          const classId = String((t as any)?.class_id ?? (t as any)?.classId ?? '').trim();
          const className = classId ? String(teamClassNameByIdMap[classId] ?? '').trim() : '';
          return className || '分類なし';
        })(),
        department_id: t.department_id ? String(t.department_id) : undefined,
      }));
  }, [teamsMaster, teamClassNameByIdMap]);

  // 週報連と同じチーム順を使うためのソートインデックス
  const teamSortIndexMap = useMemo(
    () => buildTeamSortIndexMap(teamsMaster),
    [teamsMaster],
  );

  // ---------- state: UIで選ばれた条件 ----------
  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedCreateByIds, setSelectedCreateByIds] = useState<string[]>([]);
  const [selectedDepts, setSelectedDepts] = useState<Suggestion[]>([]);
  const [deptTouched, setDeptTouched] = useState(false);
  const [createByTouched, setCreateByTouched] = useState(false);

  // 課IDの「最後に確定した値」を同期的に保持（空の瞬間に負けない）
  const selectedDeptIdsRef = useRef<string[]>([]);

  const setSelectedDeptsSafe = (vals: Suggestion[]) => {
    const ids = vals.map((d) => String(d.id));
    selectedDeptIdsRef.current = ids;
    setSelectedDepts(vals);
  };

  // 最新 state を参照できる ref（onChange の“初期謎呼び出し”対策）
  const selectedDeptsRef = useRef<Suggestion[]>([]);
  useEffect(() => {
    selectedDeptsRef.current = selectedDepts;
  }, [selectedDepts]);

  // 発行者ID（create_by_user_id / legacy ID / 旧wr_id）の
  // 「最後に確定した値」を同期的に保持（空の瞬間に負けない）
  const selectedCreateByIdsStableRef = useRef<string[]>([]);

  const setSelectedCreateByIdsSafe = (vals: string[]) => {
    const normalized = uniq((Array.isArray(vals) ? vals : [])
      .map((v) => String(v).trim())
      .filter((x) => x.length > 0));
    selectedCreateByIdsStableRef.current = normalized;
    setSelectedCreateByIds(normalized);
  };

  const selectedCreateByIdsKey = useMemo(() => {
    return (selectedCreateByIds ?? []).map(String).join('|');
  }, [selectedCreateByIds]);

  // ---------- キャッシュ復元フラグ ----------
  const [didHydrateFromCache, setDidHydrateFromCache] = useState(false);

  // 「deptOptions が揃ってから」反映したい deptIds
  const cachedDeptIdsRef = useRef<string[] | null>(null);

  // 「検索結果」を保存しておきたい
  const lastResultsRef = useRef<any[] | null>(null);
  const lastPersistedOverwriteCriteriaSigRef = useRef<string>('');

  // 「開いた時にキャッシュ結果を一度だけ流す」制御
  const appliedCachedResultsRef = useRef(false);

  useEffect(() => {
    const clearCachedResults = () => {
      lastResultsRef.current = null;
      appliedCachedResultsRef.current = false;
    };

    const invalidateForAccessContextChange = () => {
      clearCachedResults();
      const baseKey = stableCacheKeyRef.current ?? cacheKey;
      if (baseKey) safeLocalStorageRemove(`${baseKey}:results`);

      // 古い所属条件で見えていた週報を即時に消し、最新条件の初回検索を再実行する。
      onSearchResults([]);
      creatorSyncForceRef.current = true;
      setCreatorSyncTick((value) => value + 1);
      setDidInitialSearch(false);
    };

    window.addEventListener(WEEKLY_REPORT_UPDATED_EVENT, clearCachedResults);
    window.addEventListener(ACCESS_CONTEXT_UPDATED_EVENT, invalidateForAccessContextChange);
    return () => {
      window.removeEventListener(WEEKLY_REPORT_UPDATED_EVENT, clearCachedResults);
      window.removeEventListener(ACCESS_CONTEXT_UPDATED_EVENT, invalidateForAccessContextChange);
    };
  }, [cacheKey, onSearchResults]);

  // 初期化/復元のタイミングで飛んでくる onChange を無視する
  const suppressDeptOnChangeRef = useRef(true);
  const suppressCreateByOnChangeRef = useRef(true);

  // 記入者：ユーザー操作による onChange かどうかの判定
  const createByUserActionRef = useRef(false);

  const markCreateByUserAction = useCallback(() => {
    createByUserActionRef.current = true;

    // 同一イベントループ内だけ「ユーザー操作」とみなせれば十分
    runMicrotask(() => {
      createByUserActionRef.current = false;
    });
  }, []);


  // open中・初回自動検索が走る前だけ、初回検索に使う日付スナップショットを
  // 「cache復元後の start/end（= UIに表示されている値）」へ同期する。
  // これで「今週チェックがついてのに先週で検索される」ズレを防ぐ。
  useEffect(() => {
    if (!isOpen) return;
    if (!didHydrateFromCache) return;
    if (didInitialSearch) return;

    const sd = startDate ?? getDefaultStartDate();
    const ed = endDate ?? getDefaultEndDate();

    openDefaultDatesRef.current = { sd, ed };
  }, [isOpen, didHydrateFromCache, didInitialSearch, startDate, endDate]);


  useEffect(() => {
    if (!didHydrateFromCache) return;
    runMicrotask(() => {
      suppressDeptOnChangeRef.current = false;
      suppressCreateByOnChangeRef.current = false;
    });
  }, [didHydrateFromCache]);

  // ---------- 現在ユーザーのデフォルト課(myDeptId) ----------
  const myDeptId: string | null = useMemo(() => {
    // プロフィール（/get_user_info）の課を最優先で使う（SSO では currentUser から課が取れない）
    const profileDeptLabel = String((profile as any)?.department ?? '').trim();
    if (profileDeptLabel && deptLabelToIdMap[profileDeptLabel]) {
      return deptLabelToIdMap[profileDeptLabel];
    }

    const u: any = currentUser;
    if (!u) return null;

    const directDeptLabel = u.department || u.department_name || u.division || u.division_name || null;
    if (directDeptLabel && deptLabelToIdMap[directDeptLabel]) {
      return deptLabelToIdMap[directDeptLabel];
    }
    if (u.department_id != null) {
      return String(u.department_id);
    }
    if (u.division_id != null) {
      return String(u.division_id);
    }

    const loginEmail =
      u?.signInDetails?.loginId ||
      u?.loginId ||
      null;
    if (!loginEmail) return null;

    const matched = usersIndex.byEmail.get(normalizeEmail(loginEmail)) ?? null;

    if (!matched) return null;

    const mapped = extractDeptIdLike(matched, deptLabelToIdMap);
    if (mapped) return String(mapped);

    return null;
  }, [currentUser, profile, usersIndex, deptLabelToIdMap]);

  // ---------- 現在ユーザーのデフォルト氏名(myFullName) ----------
  const myFullName: string | null = useMemo(() => {
    const p: any = profile;
    const fromProfile = extractFullNameLike(p);
    if (fromProfile) return fromProfile;

    const u: any = currentUser;
    const fromUser = extractFullNameLike(u);
    if (fromUser) return fromUser;

    const loginEmail =
      u?.signInDetails?.loginId ||
      u?.loginId ||
      null;
    if (!loginEmail) return null;

    const matched = usersIndex.byEmail.get(normalizeEmail(loginEmail)) ?? null;
    if (!matched) return null;

    const nm = extractFullNameLike(matched);
    if (nm) return nm;

    return null;
  }, [profile, currentUser, usersIndex]);

  // ---------- effectiveDeptIds ----------
  const effectiveDeptIds = useMemo(() => {
    const picked = selectedDepts.map((d) => String(d.id));

    if (picked.length > 0) {
      return picked;
    }
    if (!deptTouched && myDeptId) {
      return [myDeptId];
    }
    return [];
  }, [selectedDepts, deptTouched, myDeptId]);

  const defaultUserDepForSearch = useMemo(() => {
    const fromProfile = String(
      (profile as any)?.division ??
      (profile as any)?.department ??
      '',
    ).trim();
    if (fromProfile) return fromProfile;

    const fromCurrentUser = String(
      (currentUser as any)?.division ??
      (currentUser as any)?.department ??
      (currentUser as any)?.department_name ??
      '',
    ).trim();
    if (fromCurrentUser) return fromCurrentUser;

    const departmentRow = (state.data as MasterResponse | null)?.departments?.find(
      (d: any) => String(d?.id ?? d?.department_id ?? '') === String(myDeptId ?? ''),
    );
    const fromDeptId = String(
      departmentRow?.value ?? departmentRow?.label ?? '',
    ).trim();
    if (fromDeptId) return fromDeptId;

    return '';
  }, [profile, currentUser, myDeptId, state.data]);

  const userDepForSearch = useMemo(() => {
    return String(defaultUserDepForSearch ?? '').trim();
  }, [defaultUserDepForSearch]);

  // 公開範囲判定に使う USER_DEP は「ログインユーザー所属課」のみを使う。
  // 画面で選択した課条件（検索フィルタ）を使うと、他課選択時に非公開が見えてしまうため。
  const effectiveUserDepForVisibility = useMemo(() => {
    const preferred = String(userDepForSearch ?? '').trim();
    if (preferred) {
      const preferredKey = preferred.toLocaleLowerCase();
      const row = ((state.data as MasterResponse | null)?.departments ?? []).find(
        (department: any) =>
          [department?.value, department?.label]
            .map((value) => String(value ?? '').trim().toLocaleLowerCase())
            .includes(preferredKey)
      );
      return String(row?.value ?? preferred).trim();
    }

    if (myDeptId) {
      const row = ((state.data as MasterResponse | null)?.departments ?? []).find(
        (department: any) => String(department?.id ?? department?.department_id ?? '') === String(myDeptId)
      );
      const byMyDept = String(row?.value ?? row?.label ?? '').trim();
      if (byMyDept) return byMyDept;
    }

    return '';
  }, [userDepForSearch, myDeptId, state.data]);

  // 記入者同期専用の課条件
  // 初期200件は課マスタの完了を待たずに開始するため、選択済み課に加えて
  // ログインユーザーから解決済みのデフォルト課も利用する。
  const creatorSyncDeptIds = useMemo(() => {
    return Array.from(new Set(effectiveDeptIds.map(String))).sort();
  }, [effectiveDeptIds]);

  const creatorSyncTeamIds = useMemo(() => {
    return Array.from(new Set((selectedTeams ?? []).map(String))).sort();
  }, [selectedTeams]);

  const creatorOptionsCriteriaKey = useMemo(() => {
    if (!startDate || !endDate) return null;
    const deptIds = uniq(creatorSyncDeptIds).sort();
    const teamIds = uniq(creatorSyncTeamIds).sort();
    const mode = deptIds.length === 0 ? 'dept_none' : 'dept_filtered';
    const teamMode = teamIds.length === 0 ? 'team_none' : 'team_filtered';
    const visibilityScope = String(effectiveUserDepForVisibility ?? '')
      .trim()
      .toLocaleLowerCase() || 'scope_none';
    return `${formatLocalDate(startDate)}|${formatLocalDate(endDate)}|${deptIds.join(',')}|${mode}|${teamIds.join(',')}|${teamMode}|${visibilityScope}`;
  }, [
    startDate,
    endDate,
    creatorSyncDeptIds,
    creatorSyncTeamIds,
    effectiveUserDepForVisibility,
  ]);

  // ---------- effectiveDeptCodes（記入者フィルタ用：課ラベル文字列） ----------
  const effectiveDeptCodes = useMemo(() => {
    const codes = selectedDepts
      .map((d) => normalizeDeptCode(d.label))
      .filter((v) => v.length > 0);

    return uniq(codes);
  }, [selectedDepts]);

  // ---------- 記入者(=作成者) 候補 ----------
  // 要件対応：
  // - 表示は /get_all_user_info（usersMaster）の氏名
  // - 検索は wr_id（weekly-required-items.creators を変換用にだけ使う）
  // - dept は「department 文字列（例: xq4 / xr2）」でフィルタできるように持つ
  // - 重複は wr_id / (deptCode+nameKey) で除外する
  const creatorOptionsRaw: CreatorOptionWithDept[] = useMemo(() => {
    const out: CreatorOptionWithDept[] = [];

    const seenWrId = new Set<string>();
    const seenNameKey = new Set<string>();

    usersMaster.forEach((u: any) => {
      const fullName = extractFullNameLike(u);
      if (!fullName) return;

      const wrId = resolveWrIdFromUserMaster(u);
      if (!wrId) return;

      const deptCode = extractDeptCodeLike(u); // 例: "xq4"
      const nameKey = normalizeNameKey(fullName);
      const dedupeKey = `${deptCode ?? ''}::${nameKey}`;

      if (seenWrId.has(String(wrId))) return;
      if (seenNameKey.has(dedupeKey)) return;

      seenWrId.add(String(wrId));
      seenNameKey.add(dedupeKey);

      out.push({
        id: String(wrId),
        value: fullName,
        department_id: deptCode,
      });
    });

    out.sort((a, b) => String(a.value).localeCompare(String(b.value), 'ja'));

    return out;
  }, [usersMaster, resolveWrIdFromUserMaster]);

  const creatorSelectionOptions = useMemo(() => {
    const byId = new Map<string, CreatorOptionWithDept>();
    creatorOptionsRaw.forEach((option) => byId.set(String(option.id), option));
    (creatorOptionsByDate ?? []).forEach((option) =>
      byId.set(String(option.id), option)
    );
    return Array.from(byId.values());
  }, [creatorOptionsRaw, creatorOptionsByDate]);

  const creatorIdSet = useMemo(() => {
    return new Set(creatorSelectionOptions.map((o) => String(o.id)));
  }, [creatorSelectionOptions]);

  const creatorOptionByIdMap = useMemo(() => {
    const m = new Map<string, CreatorOptionWithDept>();
    creatorSelectionOptions.forEach((opt) => {
      const id = String(opt.id ?? '').trim();
      if (!id) return;
      m.set(id, opt);
    });
    return m;
  }, [creatorSelectionOptions]);

  const creatorOptionOrderMap = useMemo(() => {
    const m = new Map<string, number>();
    creatorSelectionOptions.forEach((opt, idx) => {
      const id = String(opt.id ?? '').trim();
      if (!id) return;
      if (!m.has(id)) m.set(id, idx);
    });
    return m;
  }, [creatorSelectionOptions]);

  const resolveWrIdFromReportCreator = useCallback((row: any): string | null => {
    if (!row) return null;
    const anyR: any = row;

    const pickByAnyId = (raw: any): string | null => {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      if (creatorIdSet.has(s)) return s;

      const byRaw = usersIndex.anyIdToWrId.get(s);
      if (byRaw) return String(byRaw);

      const byMail = usersIndex.anyIdToWrId.get(normalizeEmail(s));
      if (byMail) return String(byMail);

      const byName = usersIndex.anyIdToWrId.get(normalizeNameKey(s));
      if (byName) return String(byName);

      return null;
    };

    const idLikeCandidates = [
      anyR.create_by_wr_id,
      anyR.createByWrId,
      anyR.create_by_id,
      anyR.createById,
      anyR.create_by_user_id,
      anyR.createByUserId,
      anyR.update_by_user_id,
      anyR.updateByUserId,
    ];

    for (const raw of idLikeCandidates) {
      const mapped = pickByAnyId(raw);
      if (mapped) return mapped;
    }

    const nameCandidates = [
      anyR.create_by,
      anyR.createBy,
      anyR.create_by_name,
      anyR.createByName,
      anyR.update_by,
      anyR.updateBy,
    ];

    for (const raw of nameCandidates) {
      const key = normalizeNameKey(String(raw ?? '').trim());
      if (!key) continue;
      const mapped = creatorWrIdByNameKey.get(key);
      if (mapped) return String(mapped);
    }

    return null;
  }, [creatorIdSet, usersIndex, creatorWrIdByNameKey]);

  const buildCreatorOptionsFromReports = useCallback((rows: any[]): CreatorOptionWithDept[] => {
    const byId = new Map<string, CreatorOptionWithDept>();
    const byNameFallback = new Map<string, CreatorOptionWithDept>();

    const findUser = (anyR: any): any | null => {
      const byIdCandidates = [
        anyR?.create_by_user_id,
        anyR?.createByUserId,
        anyR?.update_by_user_id,
        anyR?.updateByUserId,
      ];
      for (const raw of byIdCandidates) {
        const s = String(raw ?? '').trim();
        if (!s) continue;
        const hit =
          usersIndex.byAnyId.get(s) ??
          usersIndex.byEmail.get(normalizeEmail(s)) ??
          usersIndex.byNameKey.get(normalizeNameKey(s)) ??
          null;
        if (hit) return hit;
      }

      const byNameCandidates = [
        anyR?.create_by,
        anyR?.createBy,
        anyR?.create_by_name,
        anyR?.createByName,
        anyR?.update_by,
        anyR?.updateBy,
      ];
      for (const raw of byNameCandidates) {
        const key = normalizeNameKey(String(raw ?? '').trim());
        if (!key) continue;
        const hit = usersIndex.byNameKey.get(key) ?? null;
        if (hit) return hit;
      }
      return null;
    };

    const resolveDeptCode = (anyR: any, user: any | null): string | undefined => {
      const fromUser = extractDeptCodeLike(user);
      if (fromUser) return fromUser;

      const raw =
        anyR?.department_code ??
        anyR?.departmentCode ??
        anyR?.dept_code ??
        anyR?.deptCode ??
        anyR?.division_code ??
        anyR?.divisionCode ??
        anyR?.department ??
        anyR?.dept ??
        anyR?.division ??
        anyR?.department_name ??
        anyR?.dept_name ??
        anyR?.division_name ??
        null;

      const normalized = normalizeDeptCode(raw);
      return normalized || undefined;
    };

    (Array.isArray(rows) ? rows : []).forEach((row: any) => {
      const anyR: any = row;
      const name = String(anyR?.create_by ?? anyR?.createBy ?? '').trim();
      const wrId = resolveWrIdFromReportCreator(anyR);
      const user = findUser(anyR);
      const deptCode = resolveDeptCode(anyR, user);

      if (wrId) {
        const id = String(wrId);
        if (byId.has(id)) return;

        const base = creatorOptionByIdMap.get(id);
        if (base) {
          byId.set(id, { ...base });
          return;
        }

        const label =
          name ||
          creatorExternalLabelMap.get(id) ||
          creatorsWrIdLabelMap.get(id) ||
          '';

        if (!label) return;

        byId.set(id, {
          id,
          value: label,
          department_id: deptCode,
        });
        return;
      }

      if (!name) return;
      const nameKey = normalizeNameKey(name);
      if (!nameKey || byNameFallback.has(nameKey)) return;

      byNameFallback.set(nameKey, {
        id: name,
        value: name,
        department_id: deptCode,
      });
    });

    const list = Array.from(byId.values()).concat(Array.from(byNameFallback.values()));
    list.sort((a, b) => {
      const aOrder = creatorOptionOrderMap.get(String(a.id));
      const bOrder = creatorOptionOrderMap.get(String(b.id));
      if (typeof aOrder === 'number' && typeof bOrder === 'number' && aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      if (typeof aOrder === 'number' && typeof bOrder !== 'number') return -1;
      if (typeof aOrder !== 'number' && typeof bOrder === 'number') return 1;
      return String(a.value).localeCompare(String(b.value), 'ja');
    });

    return list;
  }, [
    resolveWrIdFromReportCreator,
    usersIndex,
    creatorOptionByIdMap,
    creatorExternalLabelMap,
    creatorsWrIdLabelMap,
    creatorOptionOrderMap,
  ]);

  // 記入者などの「最後に見えたラベル」を親側でも保持
  const createByLastLabelByIdRef = useRef<Map<string, string>>(new Map());
  const createByLastOptionByIdRef = useRef<Map<string, CreatorOptionWithDept>>(new Map());

  useEffect(() => {
    creatorSelectionOptions.forEach((o: any) => {
      const id = String(o?.id ?? '').trim();
      const label = String(o?.value ?? '').trim();
      if (id && label) {
        createByLastLabelByIdRef.current.set(id, label);
        createByLastOptionByIdRef.current.set(id, o as CreatorOptionWithDept);
      }
    });

    // creators 側のラベルも控えておく（usersMaster が欠けても “不明” を減らす）
    creatorsWrIdLabelMap.forEach((label, id) => {
      const sid = String(id).trim();
      const slabel = String(label ?? '').trim();
      if (sid && slabel && !createByLastLabelByIdRef.current.has(sid)) {
        createByLastLabelByIdRef.current.set(sid, slabel);
      }
    });
  }, [creatorSelectionOptions, creatorsWrIdLabelMap]);

  // ---------- 選択値を wr_id に正規化（email/name → wr_id） ----------
  // 要件：wr_id 変換は weekly-required-items.creators を使用
  const coerceCreateByIdsToWrIds = useCallback((vals: string[]) => {
    const out: string[] = [];

    (Array.isArray(vals) ? vals : []).forEach((raw) => {
      const s = String(raw ?? '').trim();
      if (!s) return;

      // 1) すでに wr_id（options に存在）なら採用
      if (creatorIdSet.has(s) || createByLastOptionByIdRef.current.has(s)) {
        out.push(s);
        return;
      }

      // 2) 「氏名」っぽい文字列なら creators(label) -> wr_id
      const mappedByName =
        creatorWrIdByNameKey.get(normalizeNameKey(s)) ??
        null;

      if (mappedByName) {
        out.push(String(mappedByName));
        return;
      }

      // 3) email なら usersMaster を引いて氏名 -> wr_id
      const keyAsEmail = normalizeEmail(s);
      const user = usersIndex.byEmail.get(keyAsEmail) ?? null;
      const mapped = resolveWrIdFromUserMaster(user);

      if (mapped) {
        out.push(String(mapped));
      }
    });

    return uniq(out);
  }, [creatorIdSet, creatorWrIdByNameKey, usersIndex, resolveWrIdFromUserMaster]);

  const createByOptionsSig = useMemo(() => {
    const deptKey = effectiveDeptCodes.join(',');
    const byDateKey = (creatorOptionsByDate ?? [])
      .map((opt) => `${String(opt.id)}:${String(opt.department_id ?? '')}`)
      .join('|');
    return `${didFetchUsersMaster ? 1 : 0}:${deptKey}:${creatorOptionsRaw.length}:${byDateKey}`;
  }, [didFetchUsersMaster, effectiveDeptCodes, creatorOptionsRaw.length, creatorOptionsByDate]);

  useEffect(() => {
    if (!didHydrateFromCache) return;

    suppressCreateByOnChangeRef.current = true;
    runMicrotask(() => {
      suppressCreateByOnChangeRef.current = false;
    });
  }, [createByOptionsSig, didHydrateFromCache]);

  // ---------- マスタ取得処理 ----------
  useEffect(() => {
    apiClient
      .get<MasterResponse>('/weekly-required-items', {
        params: { include_creators: false },
      })
      .then((r) => {
        setState({ loading: false, error: null, data: r.data });
      })
      .catch((e) =>
        setState({
          loading: false,
          error: e instanceof Error ? e.message : 'Unknown error',
          data: null,
        }),
      );

    apiClient
      .get<any[]>('/get_all_teams')
      .then((r) => {
        setTeamsMaster(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('チーム取得エラー:', e);
        setTeamsMaster([]);
      });

    apiClient
      .get<any[]>('/get_all_team_classfications')
      .then((r) => {
        const data = r?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.team_classfications)
            ? (data as any).team_classfications
            : Array.isArray((data as any)?.team_classifications)
              ? (data as any).team_classifications
              : Array.isArray((data as any)?.list)
                ? (data as any).list
                : Array.isArray((data as any)?.data)
                  ? (data as any).data
                  : [];
        setTeamClassificationsMaster(list);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('チーム分類取得エラー:', e);
        setTeamClassificationsMaster([]);
      });

    shareInFlightRequest(
      'get-all-user-info:all',
      () => apiClient.get<any[]>('/get_all_user_info'),
    )
      .then((r) => {
        setUsersMaster(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('ユーザー取得エラー:', e);
        setUsersMaster([]);
      })
      .finally(() => {
        setDidFetchUsersMaster(true);
      });
  }, []);

  // ---------- キャッシュ復元（検索条件 + 検索結果） ----------
  useEffect(() => {
    if (didHydrateFromCache) return;

    const key = stableCacheKeyRef.current ?? cacheKey;

    if (!key) {
      return;
    }

    const cache = readCache(key);

    if (cache?.criteria) {
      const c = cache.criteria;
      lastPersistedOverwriteCriteriaSigRef.current = buildCriteriaSig(c);

      setFreeWord(typeof c.freeWord === 'string' ? c.freeWord : '');

      setSelectedReportTypes(Array.isArray(c.selectedReportTypes) ? c.selectedReportTypes : []);
      setSelectedTeams(Array.isArray(c.selectedTeams) ? c.selectedTeams : []);

      (Array.isArray(c.selectedCreateByOptions) ? c.selectedCreateByOptions : []).forEach(
        (option) => {
          const id = String(option?.id ?? '').trim();
          const value = String(option?.value ?? '').trim();
          if (!id || !value || value === UNKNOWN_CREATOR_LABEL) return;
          const normalizedOption: CreatorOptionWithDept = {
            id,
            value,
            department_id: option.department_id,
            user_id: option.user_id,
            legacy: Boolean(option.legacy),
          };
          createByLastLabelByIdRef.current.set(id, value);
          createByLastOptionByIdRef.current.set(id, normalizedOption);
        },
      );

      const hydratedCreateByIds = Array.isArray(c.selectedCreateByIds)
        ? c.selectedCreateByIds.map((x) => String(x))
        : [];
      selectedCreateByIdsStableRef.current = hydratedCreateByIds;
      setSelectedCreateByIds(hydratedCreateByIds);

      const deptIds = Array.isArray(c.selectedDeptIds) ? c.selectedDeptIds : [];
      cachedDeptIdsRef.current = deptIds;
      selectedDeptIdsRef.current = deptIds;

      setDeptTouched(Boolean(c.deptTouched));
      setCreateByTouched(Boolean(c.createByTouched));
      
      // 開始日付プリセット（今週/先週/来週）の復元
      const presetRaw =
        c.startDatePreset ??
        (cache as any).startDatePreset;

      const preset: StartDatePreset =
        presetRaw === 'this' || presetRaw === 'last' || presetRaw === 'next'
          ? presetRaw
          : null;

      const cachedStartDate = c.startDate ? parseLocalDate(c.startDate) : null;
      const cachedEndDate = c.endDate ? parseLocalDate(c.endDate) : null;
      const cachedDateSearchMode: DateSearchMode =
        c.dateSearchMode === 'recent' || c.dateSearchMode === 'range'
          ? c.dateSearchMode
          : preset
            ? 'recent'
            : 'range';
      const activePreset = cachedDateSearchMode === 'recent' ? preset : null;

      setStartDatePreset(activePreset);
      if (activePreset) {
        setDateSearchMode('recent');
        recentPresetRef.current = activePreset;
      } else {
        setDateSearchMode('range');
        setEndDatePreset(null);
        if (
          cachedStartDate &&
          cachedEndDate &&
          stripTime(cachedEndDate) >= stripTime(cachedStartDate)
        ) {
          setStartDate(cachedStartDate);
          setEndDate(cachedEndDate);
        }
      }

      if (activePreset === 'this') {
        const { start, end } = getRecentPresetDateRange('this');

        setStartDate(start);
        setEndDate(end);
        setEndDatePreset(null);
      } else if (activePreset === 'last') {
        const { start, end } = getRecentPresetDateRange('last');

        setStartDate(start);

        setEndDate(end);

        // endDatePreset を持っているなら整合のため外す
        setEndDatePreset(null);
      } else if (activePreset === 'next') {
        const { start, end } = getRecentPresetDateRange('next');

        setStartDate(start);
        setEndDate(end);
        setEndDatePreset(null);
      } else {
        if (cachedStartDate) {
          setStartDate(cachedStartDate);
        }
        if (cachedEndDate) {
          setEndDate(cachedEndDate);
          setEndDatePreset(null);
        }
      }

      if (Array.isArray(cache.results)) {
        lastResultsRef.current = cache.results;
      }
    }

    setDidHydrateFromCache(true);
  }, [cacheKey, didHydrateFromCache, readCache, buildCriteriaSig]);

  // ---------- deptOptions が揃ったら、cachedDeptIds を Suggestion[] に変換して適用 ----------
  useEffect(() => {
    if (!didHydrateFromCache) return;
    if (state.loading || !state.data) return;
    if (!cachedDeptIdsRef.current) return;

    const ids = cachedDeptIdsRef.current;
    const mapped = deptOptions.filter((d) => ids.includes(String(d.id)));

    setSelectedDeptsSafe(mapped);

    cachedDeptIdsRef.current = null;

    suppressDeptOnChangeRef.current = true;
    runMicrotask(() => {
      suppressDeptOnChangeRef.current = false;
    });
  }, [didHydrateFromCache, state.loading, state.data, deptOptions]);

  // ---------- キャッシュ結果の反映（モーダル open 時に一度だけ） ----------
  useEffect(() => {
    if (!isOpen) {
      appliedCachedResultsRef.current = false;
      return;
    }
    if (appliedCachedResultsRef.current) return;

    if (lastResultsRef.current && Array.isArray(lastResultsRef.current)) {
      onSearchResults(lastResultsRef.current);
      appliedCachedResultsRef.current = true;
    }
  }, [isOpen, onSearchResults]);

  // ---------- キャッシュ保存（検索条件） ----------
  const persistCache = useCallback((opts?: { results?: any[] | null }) => {
    const baseKey = stableCacheKeyRef.current ?? cacheKey;
    if (!baseKey) return;
    if (!didHydrateFromCache) return;
    if (typeof window === 'undefined') return;
    const criteriaKey = `${baseKey}:criteria`;
    const resultsKey = `${baseKey}:results`;

    const shouldPersistRangeDates =
      dateSearchMode === 'range' &&
      !startDatePreset &&
      startDate != null &&
      endDate != null &&
      stripTime(endDate) >= stripTime(startDate);

    const selectedCreateByOptions = selectedCreateByIdsStableRef.current
      .map((rawId) => {
        const id = String(rawId ?? '').trim();
        const option = createByLastOptionByIdRef.current.get(id);
        const value = String(
          option?.value ?? createByLastLabelByIdRef.current.get(id) ?? '',
        ).trim();
        if (!id || !value || value === UNKNOWN_CREATOR_LABEL) return null;
        return {
          id,
          value,
          department_id: option?.department_id,
          user_id: option?.user_id,
          legacy: Boolean(option?.legacy),
        };
      })
      .filter((option): option is NonNullable<typeof option> => option != null);

    const criteria: OverwriteSearchCriteria = {
      startDate: shouldPersistRangeDates ? formatLocalDate(startDate) : null,
      endDate: shouldPersistRangeDates ? formatLocalDate(endDate) : null,
      freeWord,
      selectedReportTypes,
      selectedTeams,
      dateSearchMode,

      // チェック状態を保存（null も保存される）
      startDatePreset,

      //  専用APIの発行者ID（または移行前の旧wr_id）を保存
      selectedCreateByIds: selectedCreateByIdsStableRef.current,
      selectedCreateByOptions,

      selectedDeptIds: selectedDeptIdsRef.current,

      deptTouched,
      createByTouched,
    };
    const criteriaSig = buildCriteriaSig(criteria);

    const payload: OverwriteSearchCriteriaCacheV2 = {
      version: OVERWRITE_SEARCH_CACHE_VERSION,
      savedAt: new Date().toISOString(),
      criteria,
    };

    const payloadJson = JSON.stringify(payload);
    let ok = safeLocalStorageSet(criteriaKey, payloadJson);
    if (!ok) {
      safeLocalStorageRemove(resultsKey);
      ok = safeLocalStorageSet(criteriaKey, payloadJson);
    }
    if (!ok) return;

    const hasResultsOption =
      opts != null && Object.prototype.hasOwnProperty.call(opts, 'results');
    if (!hasResultsOption) {
      if (
        lastPersistedOverwriteCriteriaSigRef.current &&
        lastPersistedOverwriteCriteriaSigRef.current !== criteriaSig
      ) {
        safeLocalStorageRemove(resultsKey);
        lastResultsRef.current = null;
      }
      lastPersistedOverwriteCriteriaSigRef.current = criteriaSig;
      return;
    }

    lastPersistedOverwriteCriteriaSigRef.current = criteriaSig;
    const results = opts?.results;
    if (results == null) {
      safeLocalStorageRemove(resultsKey);
      lastResultsRef.current = null;
      return;
    }

    const resultsPayload: OverwriteSearchResultsCacheV3 = {
      version: OVERWRITE_SEARCH_RESULTS_CACHE_VERSION,
      savedAt: new Date().toISOString(),
      criteriaSig,
      results,
    };
    const resultsJson = JSON.stringify(resultsPayload);
    if (resultsJson.length > OVERWRITE_SEARCH_RESULTS_CACHE_MAX_CHARS) {
      safeLocalStorageRemove(resultsKey);
      return;
    }

    if (!safeLocalStorageSet(resultsKey, resultsJson)) {
      safeLocalStorageRemove(resultsKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cacheKey,
    didHydrateFromCache,
    // これが無いと「外したのに last/this が保存され続ける」
    startDatePreset,
    dateSearchMode,
    startDate,
    endDate,
    freeWord,
    selectedReportTypes,
    selectedTeams,
    deptTouched,
    createByTouched,
  ]);

  useEffect(() => {
    if (!didHydrateFromCache) return;
    persistCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    didHydrateFromCache,
    cacheKey,
    dateSearchMode,
    startDate,
    endDate,
    freeWord,
    selectedReportTypes,
    selectedTeams,
    selectedCreateByIds,
    selectedDepts,
    deptTouched,
    createByTouched,
    didFetchUsersMaster,
    persistCache,
    startDatePreset,
  ]);

  // ---------- 初期セット（前回の課条件を優先し、無い場合だけ自分の課） ----------
  useEffect(() => {
    if (!didHydrateFromCache) return;
    if (state.loading || !state.data) return;
    if (selectedDeptIdsRef.current.length > 0) return;
    if (selectedDepts.length > 0) return;
    if (deptTouched) return;

    if (myDeptId) {
      const meDept = deptOptions.find(
        (d) => String(d.id) === String(myDeptId),
      );
      if (meDept) {
        setSelectedDeptsSafe([meDept]);

        suppressDeptOnChangeRef.current = true;
        runMicrotask(() => {
          suppressDeptOnChangeRef.current = false;
        });
      }
    }
  }, [
    didHydrateFromCache,
    state.loading,
    state.data,
    myDeptId,
    deptOptions,
    selectedDepts.length,
    deptTouched,
  ]);

  const myDedicatedCreatorOption = useMemo<CreatorOptionWithDept | null>(() => {
    const loginId = normalizeEmail(normalizedLoginId ?? '');
    if (loginId) {
      const match = (creatorOptionsByDate ?? []).find(
        (option) => normalizeEmail(option.user_id ?? '') === loginId
      );
      if (match) return match;
    }

    const nameKey = normalizeNameKey(myFullName ?? '');
    if (nameKey) {
      const legacyMatches = (creatorOptionsByDate ?? []).filter(
        (option) => option.legacy && normalizeNameKey(option.value) === nameKey
      );
      if (legacyMatches.length === 1) return legacyMatches[0];
    }

    // 期間内に本人の週報がなく候補に含まれない場合も、従来どおり
    // 本人のcreate_by_user_idで初回検索し、同課全員の検索には広げない。
    if (!loginId) return null;
    return {
      id: loginId,
      value: String(myFullName ?? '').trim() || loginId,
      user_id: loginId,
      legacy: false,
    };
  }, [creatorOptionsByDate, normalizedLoginId, myFullName]);

  // ---------- 初期セット（最初だけログインユーザーを自動選択） ----------
  useEffect(() => {
    if (!didHydrateFromCache) return;
    if (state.loading || !state.data) return;
    if (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey) return;

    if (selectedCreateByIdsStableRef.current.length > 0) return;
    if (selectedCreateByIds.length > 0) return;
    if (createByTouched) return;

    // 専用APIのcreate_by_user_id一致を最優先し、同姓同名を誤選択しない。
    if (myDedicatedCreatorOption) {
      const creatorId = String(myDedicatedCreatorOption.id);
      createByLastLabelByIdRef.current.set(
        creatorId,
        String(myDedicatedCreatorOption.value)
      );
      createByLastOptionByIdRef.current.set(creatorId, myDedicatedCreatorOption);
      setSelectedCreateByIdsSafe([creatorId]);
      return;
    }

  }, [
    didHydrateFromCache,
    state.loading,
    state.data,
    creatorOptionsResolvedKey,
    creatorOptionsCriteriaKey,
    myDedicatedCreatorOption,
    selectedCreateByIds.length,
    createByTouched,
  ]);

  // ---------- モーダル初回オープン時だけ自動検索 ----------
  useEffect(() => {
    if (!isOpen) {
      setDidInitialSearch(false);
    }
  }, [isOpen]);

  const initialSearchReady = useMemo(() => {
    if (hasDateError) return false;
    if (isDateRangeIncomplete) return false;
    if (!didHydrateFromCache) return false;
    if (!didFetchUsersMaster) return false;
    if (!isReportTypeAccessReady) return false;
    if (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey) return false;
    if (creatorSelectionNormalizedKey !== creatorOptionsCriteriaKey) return false;

    // 公開範囲判定に使うユーザー情報の復元を待つ。
    // ここを待たないと、API では週報が返っているのに
    // effectiveUserDepForVisibility が空のまま表示フィルタで落ちる可能性がある。
    if (!profileLoaded) return false;

    return true;
  }, [
    hasDateError,
    isDateRangeIncomplete,
    didHydrateFromCache,
    didFetchUsersMaster,
    isReportTypeAccessReady,
    creatorOptionsResolvedKey,
    creatorOptionsCriteriaKey,
    creatorSelectionNormalizedKey,
    profileLoaded,
  ]);

  useEffect(() => {
    if (!isOpen || didInitialSearch || state.loading || !initialSearchReady) {
      return;
    }

    setDidInitialSearch(true);
    const sd = openDefaultDatesRef.current?.sd ?? getDefaultStartDate();
    const ed = openDefaultDatesRef.current?.ed ?? getDefaultEndDate();

    Promise.resolve().then(() => {
      handleSearch({ startDate: sd, endDate: ed });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    didInitialSearch,
    state.loading,
    initialSearchReady,
    hasDateError,
    isDateRangeIncomplete,
    didHydrateFromCache,
    didFetchUsersMaster,
    isReportTypeAccessReady,
    userDepForSearch,
    effectiveUserDepForVisibility,
    effectiveDeptIds,
    selectedReportTypes,
  ]);

  // ---------- 各セレクトの候補を課で絞り込む ----------
  // ---------- 記入者候補（期間 + 課 + チーム） ----------
  const progressiveCreatorOptions = useProgressiveCreatorOptions({
    enabled:
      isOpen &&
      Boolean(startDate && endDate) &&
      !hasDateError &&
      didHydrateFromCache &&
      profileLoaded,
    criteriaKey: creatorOptionsCriteriaKey,
    loginId: normalizedLoginId,
    startDate: startDate ? formatLocalDate(startDate) : '',
    endDate: endDate ? formatLocalDate(endDate) : '',
    departmentIds: creatorSyncDeptIds,
    teamIds: creatorSyncTeamIds,
    includeTemporary: true,
    userDep: effectiveUserDepForVisibility || undefined,
    userTeamId: profile?.preferredTeamId,
    revalidateToken: creatorSyncTick,
    forceRevalidateRef: creatorSyncForceRef,
    latestUpdateMs: readLatestWeeklyDataInvalidationMs(),
    legacyCacheKey: creatorOptionsCriteriaKey
      ? `${creatorOptionsLocalStorageBaseKey}::${creatorOptionsCriteriaKey}`
      : null,
    legacyCacheVersion: OVERWRITE_SEARCH_CREATOR_OPTIONS_CACHE_VERSION,
    setOptions: setCreatorOptionsByDate,
    setLoadingRemaining: setCreatorOptionsLoadingRemaining,
    setResolvedKey: setCreatorOptionsResolvedKey,
  });

  const teamOptionsFiltered: Option[] = useMemo(() => {
    if (effectiveDeptIds.length === 0) {
      return allTeamOptionsRaw;
    }
    return allTeamOptionsRaw.filter((t) => {
      return (
        (t as any).department_id &&
        effectiveDeptIds.includes(String((t as any).department_id))
      );
    });
  }, [allTeamOptionsRaw, effectiveDeptIds]);

  // 専用APIの結果は期間・課で既に絞り込み済み。
  // API取得前のマスタfallbackだけ、従来どおり課コードで絞り込む。
  const createByOptionsFiltered: Option[] = useMemo(() => {
    const source = creatorOptionsByDate ?? [];
    const nameCounts = new Map<string, number>();
    source.forEach((option) => {
      const name = String(option.value ?? '').trim();
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    });
    return source.map((option) => {
      const name = String(option.value ?? '').trim();
      if ((nameCounts.get(name) ?? 0) < 2) return option as Option;
      const discriminator = String(option.user_id ?? option.id).trim();
      return {
        ...option,
        displayValue: `${name}（${discriminator}）`,
      } as Option;
    });
  }, [creatorOptionsByDate]);

  // 旧criteria cache / 初期値の週報IDを、専用APIのuser_id/legacy IDへ移行する。
  useEffect(() => {
    if (creatorOptionsByDate == null) return;
    if (creatorOptionsLoadingRemaining) return;
    if (!creatorOptionsCriteriaKey) return;
    if (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey) return;

    const newIds = new Set(creatorOptionsByDate.map((option) => String(option.id)));
    const newIdsByName = new Map<string, string[]>();
    creatorOptionsByDate.forEach((option) => {
      const nameKey = normalizeNameKey(String(option.value ?? ''));
      if (!nameKey) return;
      const ids = newIdsByName.get(nameKey) ?? [];
      ids.push(String(option.id));
      newIdsByName.set(nameKey, ids);
    });
    const oldNameById = new Map<string, string>();
    creatorOptionsRaw.forEach((option) => {
      oldNameById.set(String(option.id), String(option.value ?? ''));
    });

    const source =
      selectedCreateByIdsStableRef.current.length > 0
        ? selectedCreateByIdsStableRef.current
        : selectedCreateByIds;
    const migrated = source.flatMap((rawId) => {
      const id = String(rawId);
      if (newIds.has(id)) return [id];

      // 既に専用API由来と分かっているuser_id/legacy IDは、候補外でも
      // 別の同名ユーザーへ置換せずそのまま保持する。
      const knownOption = createByLastOptionByIdRef.current.get(id);
      const knownLabel = String(knownOption?.value ?? '').trim();
      if (
        knownLabel &&
        knownLabel !== UNKNOWN_CREATOR_LABEL &&
        (knownOption?.user_id || knownOption?.legacy)
      ) {
        return [id];
      }

      // user masterでメールアドレスと確定できるIDは候補外でも安全に検索できる。
      const knownUser = usersIndex.byEmail.get(normalizeEmail(id));
      if (knownUser) {
        const value = extractFullNameLike(knownUser) || id;
        createByLastLabelByIdRef.current.set(id, value);
        createByLastOptionByIdRef.current.set(id, {
          id,
          value,
          user_id: id,
          legacy: false,
        });
        return [id];
      }

      // 氏名を確実に復元できる旧wr_idだけを移行対象にする。
      const oldName = oldNameById.get(id);
      if (!oldName) return [];
      const matches = newIdsByName.get(normalizeNameKey(oldName)) ?? [];
      if (matches.length === 1) return [matches[0]];

      // 同姓同名や候補外の旧wr_idは誤った人へ変換できないため解除する。
      return [];
    });

    if (migrated.join('|') !== source.map(String).join('|')) {
      selectedCreateByIdsStableRef.current = migrated;
      setSelectedCreateByIds(migrated);
    }
    setCreatorSelectionNormalizedKey(creatorOptionsCriteriaKey);
  }, [
    creatorOptionsByDate,
    creatorOptionsRaw,
    selectedCreateByIds,
    creatorOptionsCriteriaKey,
    creatorOptionsResolvedKey,
    creatorOptionsLoadingRemaining,
    usersIndex,
  ]);

  // 週報分類（部署で絞った raw）
  const reportOptionsRawByDept = useMemo(() => {
    if (effectiveDeptIds.length === 0) return reportOptionsRaw;
    return reportOptionsRaw.filter((r: any) => {
      return r.department_id && effectiveDeptIds.includes(String(r.department_id));
    });
  }, [reportOptionsRaw, effectiveDeptIds]);

  const reportTypePreferredIdByLabel = useMemo(
    () => buildPreferredReportTypeIdByLabel(reportOptionsRawByDept, effectiveDeptIds, myDeptId),
    [reportOptionsRawByDept, effectiveDeptIds, myDeptId]
  );

  // UI表示用：重複をまとめた 週報分類 options（id=label）
  const reportTypeOptionsForUi: Option[] = useMemo(() => {
    const labels = Array.from(reportTypePreferredIdByLabel.keys());

    labels.sort((a, b) => a.localeCompare(b, 'ja'));

    return labels.map((label) => ({
      id: label,
      value: label,
    }));
  }, [reportTypePreferredIdByLabel]);

  // 互換：過去キャッシュが「id配列」の場合、ラベル配列に1回だけ寄せる
  const didNormalizeReportTypesAfterHydrateRef = useRef(false);

  useEffect(() => {
    if (!didHydrateFromCache) return;
    if (didNormalizeReportTypesAfterHydrateRef.current) return;
    // マスタ未取得のタイミングで空を防ぐ
    if ((reportOptionsRaw ?? []).length === 0) return;

    didNormalizeReportTypesAfterHydrateRef.current = true;

    setSelectedReportTypes((prev) => {
      const nextLabels: string[] = [];

      (Array.isArray(prev) ? prev : []).forEach((v) => {
        const s = String(v ?? '').trim();
        if (!s) return;

        // すでにラベルならそのまま
        if (reportTypePreferredIdByLabel.has(s)) {
          nextLabels.push(s);
          return;
        }

        // id っぽいなら raw からラベルへ変換
        const hit =
          (reportOptionsRawByDept as any[]).find((r) => String(r?.id) === s) ??
          (reportOptionsRaw as any[]).find((r) => String(r?.id) === s) ??
          null;

        const label = hit ? String(hit?.value ?? hit?.label ?? '').trim() : '';
        if (label) nextLabels.push(label);
      });

      return uniq(nextLabels);
    });
  }, [
    didHydrateFromCache,
    reportTypePreferredIdByLabel,
    reportOptionsRawByDept,
    reportOptionsRaw,
  ]);

  // 選択中IDが options に無い瞬間でもUIを崩さない（ただし “不明” を最小化）
  const createByOptionsForUi: Option[] = useMemo(() => {
    const base = Array.isArray(createByOptionsFiltered) ? createByOptionsFiltered : [];
    const baseIdSet = new Set(base.map((o) => String(o.id)));

    const selected =
      selectedCreateByIdsStableRef.current.length > 0
        ? selectedCreateByIdsStableRef.current
        : selectedCreateByIds;

    const extras: Option[] = [];

    selected.forEach((rawId) => {
      const id = String(rawId ?? '').trim();
      if (!id) return;
      if (baseIdSet.has(id)) return;

      const labelFromLast = createByLastLabelByIdRef.current.get(id) ?? '';
      const labelFromMap = creatorExternalLabelMap.get(id) ?? '';
      const lastOption = createByLastOptionByIdRef.current.get(id);

      const value = labelFromLast || labelFromMap;
      if (!value || value === UNKNOWN_CREATOR_LABEL) return;

      extras.push({
        ...(lastOption ?? {}),
        id,
        value,
      } as Option);
    });

    if (extras.length === 0) return base;

    return [...extras, ...base];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createByOptionsFiltered,
    selectedCreateByIdsKey,
    selectedCreateByIds,
    creatorExternalLabelMap,
  ]);

  // ---------- 記入者 onChange ----------
  const handleCreateByChange = useCallback((vals: string[]) => {
    const userInitiated = createByUserActionRef.current;

    const normalizedIncoming = (Array.isArray(vals) ? vals : []).map((v) => String(v));
    const normalized = coerceCreateByIdsToWrIds(normalizedIncoming);

    const incomingEmpty = normalized.length === 0;

    // 初期化/復元/候補変更で飛んでくる “[]” は選択を潰さない
    // ただしユーザー操作（クリア/最後の1件を外す等）の場合は、候補0件でも必ずクリアを反映する
    if (incomingEmpty) {
      const prevStable = selectedCreateByIdsStableRef.current;

      if (!userInitiated && prevStable.length > 0) {
        // 初期化/復元直後に飛ぶ [] を無視
        if (suppressCreateByOnChangeRef.current) {
          return;
        }

        // 候補が 0 件の瞬間に飛ぶ [] を無視（ただしユーザー操作ならクリア優先）
        if (createByOptionsFiltered.length === 0) {
          return;
        }
      }
    }

    setCreateByTouched(true);
    setSelectedCreateByIdsSafe(normalized);
  }, [createByOptionsFiltered.length, coerceCreateByIdsToWrIds]);

  // ---------- 検索 ----------
  const handleSearch = async (override?: { startDate?: Date | null; endDate?: Date | null }) => {
    const sd = override?.startDate ?? startDate;
    const ed = override?.endDate ?? endDate;

    if (startDatePickerError || endDatePickerError) {
      onSearchResults([]);
      return;
    }
    if (!sd || !ed) {
      onSearchResults([]);
      return;
    }
    if (sd && ed && stripTime(ed) < stripTime(sd)) {
      onSearchResults([]);
      return;
    }
    if (!isReportTypeAccessReady) {
      onSearchResults([]);
      return;
    }

    creatorSyncForceRef.current = true;
    setCreatorSyncTick((value) => value + 1);

    const rawReportTypes: ApiReportType[] =
      ((state.data as MasterResponse | null)?.reportTypes as ApiReportType[] | undefined) ?? [];

    const createByForSearch =
      selectedCreateByIdsStableRef.current.length > 0
        ? selectedCreateByIdsStableRef.current
        : selectedCreateByIds;
    const rememberedCreatorOptions = createByForSearch
      .map((id) => createByLastOptionByIdRef.current.get(String(id)))
      .filter((option): option is CreatorOptionWithDept => option != null);
    const safeCreatorSelection = buildSafeCreatorSearchSelection({
      selectedIds: createByForSearch,
      options: [
        ...(createByOptionsForUi as CreatorOptionWithDept[]),
        ...rememberedCreatorOptions,
      ],
      knownUserIds: usersIndex.byEmail.keys(),
    });

    // 週報分類：UI は「ラベル」を持つ可能性があるので、検索時は「該当する reportTypeId 全部」に展開する
    // - effectiveDeptIds で絞った raw を元に label -> id[] を作る
    // - selectedReportTypes が id の場合も、そのまま通せるように両対応
    const reportOptionsRawByDept =
      effectiveDeptIds.length === 0
        ? reportOptionsRaw
        : reportOptionsRaw.filter((r: any) => {
            return r.department_id && effectiveDeptIds.includes(String(r.department_id));
          });

    const reportTypeLabelToIds = new Map<string, string[]>();
    (reportOptionsRawByDept ?? []).forEach((r: any) => {
      const label = String(r?.value ?? r?.label ?? '').trim();
      const id = String(r?.id ?? '').trim();
      if (!label || !id) return;

      const cur = reportTypeLabelToIds.get(label) ?? [];
      if (!cur.includes(id)) cur.push(id);
      reportTypeLabelToIds.set(label, cur);
    });

    const selectedReportTypeKeys = uniq(
      (Array.isArray(selectedReportTypes) ? selectedReportTypes : [])
        .map((v) => String(v ?? '').trim())
        .filter((v) => v.length > 0),
    );
    const visibleReportTypeKeys = uniq(
      (reportTypeOptionsForUi ?? [])
        .map((o: any) => String(o?.id ?? o?.value ?? '').trim())
        .filter((v) => v.length > 0),
    );
    const selectedVisibleReportTypeKeys = selectedReportTypeKeys.filter((key) =>
      visibleReportTypeKeys.includes(key),
    );
    const isAllVisibleReportTypesSelected =
      visibleReportTypeKeys.length > 0 &&
      selectedVisibleReportTypeKeys.length === visibleReportTypeKeys.length &&
      visibleReportTypeKeys.every((key) => selectedVisibleReportTypeKeys.includes(key));

    const reportTypeIdsForSearch = isAllVisibleReportTypesSelected
      ? []
      : uniq(
        selectedReportTypeKeys.reduce<string[]>((acc, key) => {
          // 1) ラベル一致なら、そのラベルに紐づくIDを全て展開
          const ids = reportTypeLabelToIds.get(key);
          if (ids && ids.length > 0) {
            ids.forEach((id) => acc.push(id));
            return acc;
          }

          // 2) フォールバック：key が reportTypeId ならそのまま採用
          const isDirectId =
            (reportOptionsRaw as any[]).some((r) => String(r?.id) === key);

          if (isDirectId) acc.push(key);

          return acc;
        }, []),
      );

    const params = buildWeeklySearchParams({
      startDate: sd ? formatLocalDate(sd) : undefined,
      endDate: ed ? formatLocalDate(ed) : undefined,
      freeWord,
      // ここだけ変更：UI選択（ラベル/ID）→ 検索用ID配列
      selectedReportTypeIds: reportTypeIdsForSearch,
      allReportTypes: rawReportTypes,
      selectedTeamIds: selectedTeams,
      selectedDepartmentIds: effectiveDeptIds,

      // 検索は wr_id
      selectedCreateByValues: safeCreatorSelection.selectedValues,
      selectedCreateByUserIds: safeCreatorSelection.selectedUserIds,

      createByOptions: safeCreatorSelection.options,

      appendTemporaryFlg0: false,
    });

    if (effectiveUserDepForVisibility) {
      params.set('USER_DEP', effectiveUserDepForVisibility);
    }
    if (profile?.preferredTeamId) {
      params.set('USER_TEAM_ID', String(profile.preferredTeamId));
    }

    try {
      // 保存直後や別タブの変更を含め、検索時点の公開範囲を正本として判定する。
      // 取得または形式確認に失敗した場合は catch で結果を空にし、限定公開を広げない。
      const currentAccessMap = buildWeeklyReportAccessMap(
        await fetchLatestWeeklyReportPublicSettings(),
        rawReportTypeIdSet,
        state.data ?? {},
      );
      setReportTypeAccessById(currentAccessMap);

      // 同名の分類はチームごとに別設定。無効化された option の設定も過去週報に対して
      // 維持するため、有効な分類マスタにアクセスマップ由来の option を合成して解決する。
      const resolveEffectiveAccessForRow = (row: any): EffectiveReportTypeAccess => {
        const attrs = Array.isArray((row as any)?.attributes) ? (row as any).attributes : [];
        const reportTypeAttr = attrs.find((a: any) => {
          if (Number((a as any)?.attr_id) === 1) return true;
          const name = String((a as any)?.attr_name ?? '').trim();
          return name === CLASSIFICATION_MARKET_TARGET_ATTR_NAME;
        });

        return resolveEffectiveAccessForReportTypeValue({
          raw: (reportTypeAttr as any)?.attr_value,
          options: resolveReportTypeAccessOptions(rawReportTypes, currentAccessMap),
          departmentId: Number((row as any)?.department),
          teamId: Number((row as any)?.team),
          accessMap: currentAccessMap,
        });
      };

      const resolveRowDeptCode = (row: any): string => {
        const rowAny = row as any;
        const rawDeptCandidates = getWeeklyReportRegisteredDepartmentRawValues(rowAny);

        for (const rawDept of rawDeptCandidates) {
          const asString = String(rawDept ?? '').trim();
          if (!asString) continue;
          const fromId = deptIdToLabelMap[asString];
          if (fromId) return normalizeScopeCode(fromId);
          const normalized = normalizeScopeCode(asString);
          if (normalized) return normalized;
        }

        const teamId = String(rowAny?.team ?? '').trim();
        if (!teamId) return '';

        const teamRow = (teamsMaster ?? []).find((t: any) => {
          const id = String((t as any)?.team_id ?? (t as any)?.id ?? '').trim();
          return id === teamId;
        });
        if (!teamRow) return '';

        const teamDeptId = String(
          (teamRow as any)?.department_id ??
          (teamRow as any)?.departmentId ??
          (teamRow as any)?.dept_id ??
          '',
        ).trim();
        if (!teamDeptId) return '';

        const byDeptId = String(deptIdToLabelMap[teamDeptId] ?? '').trim();
        if (byDeptId) return normalizeScopeCode(byDeptId);
        return normalizeScopeCode(teamDeptId);
      };

      // 課内公開 (対象が空の非公開設定) は、週報の課コードで判定する。
      const applyRowDepartmentFallback = (
        setting: ReportTypeAccessSetting,
        rowDeptCode: string,
      ): ReportTypeAccessSetting =>
        setting.visibility === 'private' &&
        setting.departments.length === 0 &&
        setting.teams.length === 0 &&
        rowDeptCode.length > 0
          ? { visibility: 'dept', departments: [rowDeptCode], teams: [] }
          : setting;

      const results = await fetchOverwriteReportsWithOwnerAccess<any>({
        params,
        authenticatedUserId: currentUser?.signInDetails?.loginId,
        canAccess: (row) => {
          const access = resolveEffectiveAccessForRow(row);
          const rowDeptCode = resolveRowDeptCode(row);
          return isAccessAllowedByEffectiveAccess(access, (setting) =>
            canAccessWeeklyReportByScope(
              applyRowDepartmentFallback(setting, rowDeptCode),
              effectiveUserDepForVisibility,
              {
                deptIdToLabelMap,
                deptLabelToIdMap,
                userDeptIdRaw: myDeptId,
                userTeamIdRaw: profile?.preferredTeamId,
              },
              false,
            ),
          );
        },
      });

      const resultsWithKana = await attachKanaForWeeklyList(results);

      const sorted = [...resultsWithKana].sort(
        compareWeeklyReports({
          teamSortIndexMap,
          sortMode: 'DEFAULT',
        }),
      );

      lastResultsRef.current = sorted;
      persistCache({ results: sorted });

      onSearchResults(sorted);
    } catch {
      onSearchResults([]);
    }
  };

  /* =========================================================
   * Render
   * ======================================================= */
  const dateSearchDesktopWidth = dateSearchMode === 'recent' ? 420 : 300;
  const secondaryFilterGridSx = {
    '@media (min-width:1200px)': {
      flex: '1 1 190px',
      maxWidth: 'none',
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box
        sx={{
          width: '100%',
          maxWidth: 'none',
          p: { xs: 1, sm: 2 },
          backgroundColor: isDark ? '#1e293b' : '#f9f9fb',
          borderRadius: '12px',
        }}
      >
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
        {/* 日付 */}
        <Grid
          size={{
            xs: 12,
            sm: dateSearchMode === 'recent' ? 12 : 5.2,
            md: dateSearchMode === 'recent' ? 4.2 : 2.55,
          }}
          sx={{
            '@media (min-width:1200px)': {
              flex: `0 0 ${dateSearchDesktopWidth}px`,
              maxWidth: `${dateSearchDesktopWidth}px`,
            },
          }}
        >
          <Typography
            component="label"
            sx={{
              ...baseCustomLabelStyle,
              display: 'inline-block',
              width: 'fit-content',
              lineHeight: 1,
              marginBottom: 0,
              whiteSpace: 'nowrap',
              '@media (max-width:1550px)': {
                fontSize: '11px',
                lineHeight: 1,
              },
            }}
          >
            検索期間
          </Typography>

          <Box
            sx={{
              mt: dateSearchMode === 'recent' ? 0.35 : 0,
              display: 'flex',
              alignItems: 'stretch',
              gap: 1.2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <FormGroup
              sx={{
                mb: 0,
                mt: dateSearchMode === 'range' ? 0.75 : 0,
                px: 0.5,
                py: 0,
                borderRadius: '12px',
                border: isDark ? '1px solid #334155' : '1px solid #dbe3ee',
                backgroundColor: isDark ? '#243044' : '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexShrink: 0,
                minWidth: { xs: '100%', sm: 88 },
                '& .MuiFormControlLabel-root': {
                  margin: 0,
                  marginBottom: 0,
                  borderRadius: '8px',
                  paddingInline: 0.35,
                  transition: 'background-color 0.2s ease',
                },
                '& .MuiFormControlLabel-root:hover': {
                  backgroundColor: isDark ? '#2d3f55' : '#eef2f7',
                },
                '& .MuiFormControlLabel-root:last-of-type': {
                  marginBottom: 0,
                },
                '& .MuiFormControlLabel-label': {
                  fontSize: 11,
                  lineHeight: 1,
                  color: isDark ? '#94a3b8' : '#475569',
                },
                '& .MuiCheckbox-root': {
                  padding: 0.25,
                  color: isDark ? '#94a3b8' : '#64748b',
                  '&.Mui-checked': {
                    color: isDark ? '#38bdf8' : '#2563eb',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ paddingTop: dateSearchMode === 'recent' ? '4px' : 0 }}
                control={
                  <Checkbox
                    checked={dateSearchMode === 'recent'}
                    onChange={handleDateSearchModeChange('recent')}
                  />
                }
                label="直近"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dateSearchMode === 'range'}
                    onChange={handleDateSearchModeChange('range')}
                  />
                }
                label="期間検索"
              />
            </FormGroup>

            {dateSearchMode === 'recent' ? (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  p: { xs: 1, sm: 1.1 },
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: recentPresetTone.border,
                  backgroundColor: recentPresetTone.surface,
                  boxShadow: '0 12px 26px -22px rgba(15, 23, 42, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  transition: 'background-color 0.2s ease, border-color 0.2s ease',
                  '@media (min-width:1200px)': {
                    flex: '0 1 310px',
                    maxWidth: '310px',
                  },
                }}
              >
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={() => shiftRecentPreset(-1)}
                    disabled={currentRecentPreset === 'last'}
                    startIcon={<ChevronLeftIcon />}
                    sx={recentPresetButtonSx}
                  >
                    Prev
                  </Button>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'center',
                      px: 0.35,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box sx={{ fontSize: 14, fontWeight: 700, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                      {recentPresetLabel}
                    </Box>
                    <Box sx={{ fontSize: 12, color: isDark ? '#94a3b8' : '#475569' }}>{recentRangeLabel}</Box>
                  </Box>

                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={() => shiftRecentPreset(1)}
                    disabled={currentRecentPreset === 'next'}
                    endIcon={<ChevronRightIcon />}
                    sx={recentPresetButtonSx}
                  >
                    Next
                  </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mt: '-20px',
                  '@media (max-width:1550px)': {
                    mt: '-20px',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 24,
                    mb: '5px',
                    '@media (max-width:1550px)': {
                      minHeight: 20,
                      mb: '3px',
                    },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      ...baseCustomLabelStyle,
                      marginBottom: 0,
                      visibility: 'hidden',
                      whiteSpace: 'nowrap',
                      '@media (max-width:1550px)': {
                        fontSize: '11px',
                        lineHeight: 1,
                      },
                    }}
                  >
                    開始日付
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '14px',
                    border: isDark ? '1px solid #334155' : '1px solid #dbe3ee',
                    backgroundColor: isDark ? '#243044' : '#f8fafc',
                  }}
                >
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="span"
                    sx={{
                      ...baseCustomLabelStyle,
                      color: isDark ? '#e2e8f0' : 'rgba(0, 0, 0, 0.87)',
                      position: 'absolute',
                      top: -26,
                      left: 0,
                      pointerEvents: 'none',
                      whiteSpace: 'nowrap',
                      zIndex: 1,
                      lineHeight: 1,
                    }}
                  >
                    開始日付
                  </Box>
                  <DatePicker
                    value={startDate}
                    onChange={(newVal, context) => {
                      if ((context as any)?.validationError) return;
                      setStartDate(newVal);

                      if (startDatePreset) setStartDatePreset(null);

                      if (!newVal) return;
                      if (endDate && stripTime(newVal) > stripTime(endDate)) {
                        setEndDate(null);
                        setEndDatePreset(null);
                        setEndDatePickerOpen(true);
                      }
                    }}
                    onError={(newError) => setStartDatePickerError(newError)}
                    slots={{ day: WeeklyHighlightDay }}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: {
                        ...datePickerSlotProps.textField,
                        margin: 'none',
                        error: Boolean(startDatePickerError),
                        helperText: startDateHelperText || undefined,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                          },
                        },
                      },
                      day: {
                        highlightedWeeks,
                        rangeStartDate: startDate,
                        rangeEndDate: endDate,
                        showRangeStartMarker: Boolean(startDate && endDate),
                        showRangeEndMarker: Boolean(startDate && endDate),
                      } as any,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        height: 56,
                        minWidth: 90,
                        pr: 0,
                        pl: 0.7,
                        py: 1,
                        boxSizing: 'border-box',
                      },
                      '& .MuiIconButton-root.MuiIconButton-edgeEnd': {
                        width: 40,
                        height: 40,
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        pr: 2.5,
                        mr: 0,
                      },
                      '@media (max-width:1550px)': {
                        '& .MuiInputBase-input': {
                          height: 52,
                          minWidth: 80,
                          pl: 0.5,
                          py: 0.75,
                        },
                        '& .MuiIconButton-root.MuiIconButton-edgeEnd': {
                          width: 34,
                          height: 34,
                        },
                        '& .MuiInputAdornment-root .MuiIconButton-root': {
                          pr: 1.5,
                        },
                      },
                    }}
                  />
                </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {dateSearchMode === 'range' && (
          <Grid
            size={{ xs: 12, sm: 1, md: 0.25 }}
            sx={{
              textAlign: 'center',
              pt: elementVerticalAlignPadding,
              '@media (min-width:1200px)': {
                flex: '0 0 24px',
                maxWidth: '24px',
              },
            }}
          >
            <Typography
              variant="body1"
              component="span"
              sx={{ color: 'text.secondary', fontWeight: 'medium' }}
            >
              〜
            </Typography>
          </Grid>
        )}

        {dateSearchMode === 'range' && (
          <Grid
            size={{ xs: 12, sm: 4.8, md: 1.85 }}
            sx={{
              '@media (min-width:1200px)': {
                flex: '0 0 170px',
                maxWidth: '170px',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 24,
                mb: '5px',
                '@media (max-width:1550px)': {
                  minHeight: 20,
                  mb: '3px',
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  ...baseCustomLabelStyle,
                  marginBottom: 0,
                  visibility: 'hidden',
                  whiteSpace: 'nowrap',
                  '@media (max-width:1550px)': {
                    fontSize: '11px',
                    lineHeight: 1,
                  },
                }}
              >
                終了日付
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1,
                borderRadius: '14px',
                border: isDark ? '1px solid #334155' : '1px solid #dbe3ee',
                backgroundColor: isDark ? '#243044' : '#f8fafc',
              }}
            >
              <Box sx={{ position: 'relative' }}>
              <Box
                component="span"
                sx={{
                  ...baseCustomLabelStyle,
                  color: isDark ? '#e2e8f0' : 'rgba(0, 0, 0, 0.87)',
                  position: 'absolute',
                  top: -26,
                  left: 0,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                  lineHeight: 1,
                }}
              >
                終了日付
              </Box>
              <DatePicker
                value={endDate}
                open={endDatePickerOpen}
                onOpen={() => setEndDatePickerOpen(true)}
                onClose={() => setEndDatePickerOpen(false)}
                onChange={(newVal, context) => {
                  if ((context as any)?.validationError) return;
                  setEndDate(newVal);
                }}
                onError={(newError) => setEndDatePickerError(newError)}
                minDate={startDate ?? undefined}
                slots={{ day: WeeklyHighlightDay }}
                slotProps={{
                  ...datePickerSlotProps,
                  textField: {
                    ...datePickerSlotProps.textField,
                    margin: 'none',
                    error: Boolean(endDatePickerError) || Boolean(dateRangeErrorMsg),
                    helperText: endDateHelperText || undefined,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: isDark ? '#1e293b' : '#fff',
                      },
                    },
                  },
                  day: {
                    highlightedWeeks,
                    rangeStartDate: startDate,
                    rangeEndDate: endDate,
                    showRangeStartMarker: true,
                  } as any,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    height: 56,
                    minWidth: 90,
                    pr: 0,
                    pl: 0.7,
                    py: 1,
                    boxSizing: 'border-box',
                  },
                  '& .MuiIconButton-root.MuiIconButton-edgeEnd': {
                    width: 40,
                    height: 40,
                  },
                  '& .MuiInputAdornment-root .MuiIconButton-root': {
                    pr: 2.5,
                    mr: 0,
                  },
                  '@media (max-width:1550px)': {
                    '& .MuiInputBase-input': {
                      height: 52,
                      minWidth: 80,
                      pl: 0.5,
                      py: 0.75,
                    },
                    '& .MuiIconButton-root.MuiIconButton-edgeEnd': {
                      width: 34,
                      height: 34,
                    },
                    '& .MuiInputAdornment-root .MuiIconButton-root': {
                      pr: 1.5,
                    },
                  },
                }}
              />
              </Box>
            </Box>
          </Grid>
        )}

        {/* 課 */}
        <Grid
          size={{ xs: 12, sm: 6, md: 1.45 }}
          sx={{
            '@media (min-width:1200px)': {
              flex: '1 1 150px',
              maxWidth: 'none',
            },
          }}
        >
          {/* ★開始/終了日付と同じ「ラベル行の高さ・余白」に揃える */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 24,
              mb: '5px',

              '@media (max-width:1550px)': {
                minHeight: 20,
                mb: '3px',
              },
            }}
          >
            <Typography
              component="label"
              className="label-department"
              sx={{
                ...baseCustomLabelStyle,
                marginBottom: 0,
                whiteSpace: 'nowrap',

                '@media (max-width:1550px)': {
                  fontSize: '11px',
                  lineHeight: 1,
                },
              }}
            >
              課
            </Typography>
          </Box>

          <Box
            className="search-section-autocomplete"
            sx={{
              '& .MuiOutlinedInput-root fieldset': { borderColor: '#e0e0e0' },
              '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#bdbdbd' },
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: '#bdbdbd',
                boxShadow: 'none',
              },
              '& .MuiInputBase-root:not(.MuiAutocomplete-hasClearIcon)': {
                alignItems: 'center',
              },
              '& .MuiAutocomplete-hasClearIcon': {
                alignItems: 'flex-start',
              },
              '& .MuiInputBase-root': {
                minHeight: 56,
              },

              // 1550px以下：開始/終了日付(52px)と高さ感を揃える
              '@media (max-width:1550px)': {
                '& .MuiInputBase-root': {
                  minHeight: 52,
                },
              },
            }}
          >
            <MultiSelectWithCheckboxes
              suggestions={deptOptions}
              placeholder="-- 課を選択 --"
              initialValue={selectedDepts}
              onChange={(vals) => {
                const prevIds = selectedDeptIdsRef.current;

                if (suppressDeptOnChangeRef.current && vals.length === 0 && prevIds.length > 0) {
                  return;
                }

                setSelectedDeptsSafe(vals);

                const prev = selectedDeptsRef.current;
                if (vals.length > 0 || prev.length > 0) {
                  setDeptTouched(true);
                }

                const nextDeptIds = vals.map((d) => String(d.id));

                // 課が「条件なし（空）」になったら、子フィルタは全部クリアして「条件なし」にする
                if (nextDeptIds.length === 0) {
                  setSelectedTeams([]);
                  setSelectedReportTypes([]);
                  setSelectedCreateByIdsSafe([]);
                  setCreateByTouched(true);
                  return;
                }

                const nextTeamOptions =
                  nextDeptIds.length === 0
                    ? allTeamOptionsRaw
                    : allTeamOptionsRaw.filter((t: any) => {
                        return t.department_id && nextDeptIds.includes(String(t.department_id));
                      });

                const nextReportTypeOptionsRaw =
                  nextDeptIds.length === 0
                    ? reportOptionsRaw
                    : reportOptionsRaw.filter((r: any) => {
                        return r.department_id && nextDeptIds.includes(String(r.department_id));
                      });

                // チーム sanitize（id）
                const teamAllowed = new Set(nextTeamOptions.map((o: any) => String(o.id)));
                setSelectedTeams((prevTeams) =>
                  (Array.isArray(prevTeams) ? prevTeams : []).filter((v) => teamAllowed.has(String(v))),
                );

                // 週報分類 sanitize（ラベル）
                const reportTypeAllowedLabels = new Set(
                  (nextReportTypeOptionsRaw ?? [])
                    .map((o: any) => String(o?.value ?? o?.label ?? '').trim())
                    .filter((s: string) => s.length > 0),
                );
                setSelectedReportTypes((prevTypes) =>
                  (Array.isArray(prevTypes) ? prevTypes : []).filter((v) =>
                    reportTypeAllowedLabels.has(String(v ?? '').trim()),
                  ),
                );

                // 記入者は次の課条件に対する専用APIが到着するまで保持する。
              }}

            />
          </Box>
        </Grid>

        {/* チーム */}
        <Grid size={{ xs: 12, sm: 6, md: 1.9 }} sx={secondaryFilterGridSx}>
          <Box sx={fieldLabelRowSx}>
            <Typography component="label" sx={fieldLabelTextSx}>
              チーム
            </Typography>
          </Box>

          <Box sx={hideInnerSelectLabelSx}>
            <MultiSelectCheckbox
              label=""
              options={teamOptionsFiltered}
              selectedValues={selectedTeams}
              onChange={setSelectedTeams}
            />
          </Box>
        </Grid>

        {/* 記入者 */}
        <Grid size={{ xs: 12, sm: 6, md: 2.0 }} sx={secondaryFilterGridSx}>
          <Box sx={fieldLabelRowSx}>
            <Typography component="label" sx={fieldLabelTextSx}>
              発行者
              <Box component="span" sx={{ fontSize: '75%' }}>
                （開始-終了日付）
              </Box>
            </Typography>
          </Box>

          <Box
            sx={hideInnerSelectLabelSx}
            // クリック/キー操作/タッチ操作を「ユーザー操作」としてマーク
            onMouseDownCapture={markCreateByUserAction}
            onKeyDownCapture={markCreateByUserAction}
            onTouchStartCapture={markCreateByUserAction}
          >
            <MultiSelectCheckbox
              label=""
              options={createByOptionsForUi}
              selectedValues={
                selectedCreateByIdsStableRef.current.length > 0
                  ? selectedCreateByIdsStableRef.current
                  : selectedCreateByIds
              }
              onChange={handleCreateByChange}
              externalLabelMap={creatorExternalLabelMap}
              identityMode="id"
              maxRenderedOptions={CREATOR_OPTION_DISPLAY_LIMIT}
              maxSearchRenderedOptions={CREATOR_SEARCH_RESULT_LIMIT}
              initialRenderedOptions={100}
              renderedOptionsStep={100}
              renderAllOptionsWhenLoaded
              maxAutoRenderedOptions={CREATOR_OPTION_DISPLAY_LIMIT}
              loadedOptionCount={createByOptionsForUi.length}
              allOptionsLoaded={progressiveCreatorOptions.allOptionsLoaded}
              remoteOptions={progressiveCreatorOptions.remoteSearchOptions}
              onFilterTextChange={progressiveCreatorOptions.onSearchTextChange}
              searchLoading={progressiveCreatorOptions.searchLoading}
              searchResultsComplete={progressiveCreatorOptions.searchResultsComplete}
              searchError={progressiveCreatorOptions.searchError}
              searchLoadingText="発行者を検索中です…"
              virtualizeOptions
              countUnit="名"
              onOpen={progressiveCreatorOptions.retry}
              loading={creatorOptionsLoadingRemaining}
              loadingText={progressiveCreatorOptions.loadingText}
            />
          </Box>
        </Grid>

        {/* 週報分類 */}
        <Grid size={{ xs: 12, sm: 6, md: 2.0 }} sx={secondaryFilterGridSx}>
          <Box sx={fieldLabelRowSx}>
            <Typography component="label" sx={fieldLabelTextSx}>
              週報分類
            </Typography>
          </Box>

          <Box sx={hideInnerSelectLabelSx}>
          <MultiSelectCheckbox
            label=""
            options={reportTypeOptionsForUi}
            selectedValues={selectedReportTypes}
            onChange={setSelectedReportTypes}
          />
          </Box>
        </Grid>

          {/* フリーワード */}
          <Grid size={{ xs: 12, sm: 12, md: 3.6 }}>
            <Typography component="label" sx={customLabelStyleNoMargin}>
              フリーワード
            </Typography>
            <TextField
              value={freeWord}
              onChange={(e) => setFreeWord(e.target.value)}
              {...textFieldSharedProps}
              sx={{
                '& .MuiInputBase-input': {
                  height: 56,
                  padding: '8px 8px',
                  boxSizing: 'border-box',
                },
              }}
            />
          </Grid>

          {/* 検索ボタン */}
          <Grid
            size={{ xs: 12, sm: 6, md: 1.2 }}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              ml: 'auto',
              pr: { xs: 2.5, sm: 3.5, md: 7.3 },
              pt: elementVerticalAlignPadding,
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SearchIcon fontSize="inherit" />}
              onClick={() => {
                void handleSearch();
              }}
              disabled={hasDateError || isDateRangeIncomplete}
              sx={{
                width: 100,
                height: 40,
                mt: '5px',
                fontSize: '14px',
                '@media (max-width:1615px)': {
                  fontSize: '10px',
                },
              }}
            >
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );

};

export default OverwriteSearchArea;
